// ===========================================================================
// App orchestrator: the client state machine that ties the landing screen, the
// chat screen, the signaling client, and the WebRTC peer together.
//
// States: landing -> (token + getUserMedia + connect) -> searching -> in-call,
// returning to searching on skip / peer-left, and back to landing on stop.
// ASCII only.
// ===========================================================================

import { t } from './i18n.js';
import { mount, toast, el } from './dom.js';
import { SERVER_HTTP } from './config.js';
import { getFingerprint } from './device-signal.js';
import { getStoredAge, verifyAge } from './age.js';
import { rememberBand } from './band-memory.js';
import { getKeepsake, storeKeepsake } from './keepsake.js';
import { Signaling } from './signaling.js';
import { PeerConnection } from './rtc.js';
import { SfuPeer } from './sfu-client.js';
import { renderLanding } from './ui/landing.js';
import { createAudioEnhancer, createVideoEnhancer, createFocusSegmenter } from './media-effects.js';
import { renderChat } from './ui/chat.js';
import { renderVerify } from './ui/verify.js';
import { renderSupport } from './ui/support.js';
import { renderRooms, renderGroupChat } from './ui/rooms.js';
import { AccountClient } from './account.js';
import { openBilling } from './ui/billing.js';
import { openAccount } from './ui/account-panel.js';
import { openRegulars, createBell, showNoticeToast } from './ui/regulars.js';
import { showCallingSheet, showOfferSheet, setCallChrome, callErrorText } from './ui/call-sheets.js';
import { createCommunityClient, renderCommunityCard, openCommunityLanding, renderCommunitySection, renderCommunityPanel } from './ui/community.js';
import { newMessageId } from './ui/message-ui.js';
import { playJoinTone, playNoticeTone, primeAudio, getJoinSoundOn } from './tones.js';
import { startTabAttention, noteMessage, noteMatched } from './tab-attention.js';
import { ClientMsg, ServerMsg, ChatMode, isShareableFile, videoProvider, VIDEO_SLOTS, GROUP_VIDEO_MAX_BITRATE } from '@lumi/shared';


/**
 * Try to obtain a session token. Returns { status: 'ok', token, policy, ... },
 * or { status: 'verification_required' } when the server wants an age check, or
 * throws for a ban / bad input / outage.
 */
async function obtainSession(birthYear, fingerprint, ageToken, accountToken) {
  const res = await fetch(`${SERVER_HTTP}/api/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // The keepsake rides along so the server can recognize this device (see
    // keepsake.js); undefined when there is none, and the server issues one.
    body: JSON.stringify({ birthYear, fingerprint, ageToken, accountToken, keepsake: getKeepsake()?.key }),
  });
  if (res.status === 403) {
    const body = await res.json().catch(() => ({}));
    if (body.error === 'under_age') {
      throw new Error(body.message || 'You must be at least 13 to use Lumi.');
    }
    throw new Error('Access to Lumi has been restricted from this device.');
  }
  if (res.status === 400) throw new Error('Please choose a valid birth year.');
  if (res.status === 401) {
    const body = await res.json().catch(() => ({}));
    if (body.error === 'verification_required') return { status: 'verification_required' };
    throw new Error('Lumi is unavailable right now. Please try again.');
  }
  if (!res.ok) throw new Error('Lumi is unavailable right now. Please try again.');
  const body = await res.json();
  // Present only on issuance: a fresh key, or a replacement after the band
  // changed. Storing it is what lets the next visit be the same person.
  if (body.keepsake) storeKeepsake(body.keepsake);
  return { status: 'ok', ...body };
}

export function createApp(root) {
  // Count messages that arrive while this tab is behind something else, and
  // put the count in the tab title. Started once, here, because the listeners
  // are on document and window and outlive every screen.
  startTabAttention();
  let signaling = null;
  let peer = null;
  let localStream = null; // raw capture (self-view)
  // Client-side media effects (applied to your own stream before it is sent).
  let audioEnhancer = null; // noise-suppression node wrapper (#9)
  let videoEnhancer = null; // canvas processor for blur (#8) + filters (#11)
  let rawCamTrack = null; // the unprocessed camera track (to swap back to)
  const effectPrefs = (() => {
    try {
      return JSON.parse(localStorage.getItem('lumi.effects') || '{}') || {};
    } catch {
      return {};
    }
  })();
  effectPrefs.filter = effectPrefs.filter || 'none';
  let sendStream = null; // what we send to the peer (mic routed through a gain node)
  let audioCtx = null;
  let micGain = null;
  let iceConfig = null;
  let chatApi = null;
  let selfId = '';
  let remoteReady = false;
  let pendingIce = [];
  // SFU (mediasoup) media path. When the server runs an SFU, media is relayed
  // through it (peers never see each other's IP) instead of peer-to-peer.
  let sfuEnabled = false;
  let sfuPeer = null;
  let sfuReqId = 0;
  const sfuPending = new Map(); // reqId -> { resolve, reject }
  let intent = '1on1'; // '1on1' | 'group'
  // What they picked on the start screen. The rooms hub opens on this, so
  // choosing Text and then pressing Group does not hand back a video room.
  let chosenMode = ChatMode.VIDEO;
  let handle = null; // optional display name the user picked
  let interest = null; // optional interests text (for interest matching)
  let videoAllowed = true; // whether this band may turn the camera on (policy)
  let myBand = null; // 'adult' | 'minor', from the session; decides whether links off Lumi are clickable
  let isModerator = false; // admin-granted peer-moderation power (kick for 15 min)
  let isStaff = false; // verified Lumi staff (admin-granted)
  // True while Lumi staff have a TEXT drop-in open with this session. While it
  // is, what you type goes to them rather than into the void -- you have no
  // partner yet, which is the whole reason they are talking to you.
  let adminChatOpen = false;
  let isPlusMember = false; // Lumi Plus perks active for this session
  let moderateImages = false; // server has an image moderator -> sample camera frames
  let modSampler = null; // interval handle for camera-frame sampling
  let modSampleVideo = null; // offscreen <video> the sampler draws from
  let asrEnabled = false; // server has speech-to-text -> sample audio (moderation + captions)
  let audioActive = false; // audio sampler loop running
  let audioTimer = null; // next-clip timer
  let audioRecorder = null; // the in-flight MediaRecorder
  let roomsApi = null; // the rooms-hub api, when browsing rooms
  let groupApi = null; // the group-room screen api, when in a group room
  let currentRoomMode = ChatMode.VIDEO; // the kind of room you are in, if any
  // Whether this room's video has been announced in the chat yet. Lives out
  // here because entering a room resets it and the handler reads it.
  let sawVideoState = false;
  let currentRoom = null; // the joined room descriptor
  let joinCode = null; // a group invite code parsed from the URL (/r/CODE), if any
  // "Come and talk to me": the code somebody arrived WITH (/i/CODE), and the
  // one this person can share. Two different things that must not be confused
  // -- the first is used once at HELLO, the second is shown for as long as
  // they are here.
  let arrivingInvite = null;
  let myInviteCode = null;
  let invitesAllowed = false; // from WELCOME: may this band hand out a link
  const roomHandles = new Map(); // peerId -> handle (group rooms)
  const mutedPeers = new Set(); // group peers I have muted (hide their media/text/files)
  const account = new AccountClient(); // optional adult account (Plus/Room Pro/cosmetics)
  let maxVideoBitrate = 0; // personal bitrate ceiling from WELCOME (a Plus perk)
  let videoSlots = null; // the group room's camera budget: { used, max, holders }
  // Regulars. `social` is WELCOME's word on whether the wave exists for this
  // band; the server refuses either way, so the client only decides what to
  // show. `lastPairId` is the chat that just ended -- the only one a wave is
  // for. The bells (chat header, rooms hub) share one unread count.
  let social = false;
  let messagesOn = false; // WELCOME's word on whether a regular's row gets a Message button
  let lastPairId = null;
  let regularsState = { regulars: [], invisible: false, quietHours: null }; // the last REGULARS
  let regularsPanel = null; // the open panel's api, while it is open
  let unreadNotices = 0;
  let landingRegularsCount = 0; // from /api/regulars/count, for the landing link
  const bells = new Set(); // bell apis on screen
  // ---- Calls between regulars (5.B4; ui/call-sheets.js) ----
  // `callsOn` is WELCOME's word on whether a regular can be called from here.
  // A call becomes an ordinary 1:1 pair; `inCall` is what makes its end return
  // you to your regulars rather than to the queue. `pairedNow` is whether a
  // pair is up at all, so an offer that arrives mid-chat is declined unseen.
  let callsOn = false;
  let inCall = false;
  let pairedNow = false;
  let callingSheet = null; // the "Calling..." sheet, while it is up
  let offerSheet = null; // the ringing sheet, while it is up
  let afterCall = false; // a call just ended: search again once the panel closes
  // My community. `communityCode` is a /c/CODE link waiting for a session (or
  // an Open pressed before there was one); `communityState` is the last
  // COMMUNITY_STATE for the room we are in; the panel and the card redraw
  // from it. Everything else about communities goes over HTTP.
  let communityCode = null;
  let communityState = null;
  let communityPanel = null; // the in-room panel's api, while in a community room
  let communityCard = null; // the card in the regulars panel, while it is open
  let communitySection = null; // the hub's "Your community" section, while on the hub
  const communityClient = createCommunityClient({ base: SERVER_HTTP, headers: personHeaders });
  let videoWaiting = false; // camera on, but every slot was taken when we asked
  let shareKey = null; // proves a file upload belongs to this session (from WELCOME)
  // ---- Lower-pressure starts ----
  let selfBand = 'adult'; // from WELCOME; a minor never auto-reveals, whatever the browser remembers
  let cameraStart = 'agree'; // 'agree' (default): cameras open when both tap; 'open': REVEAL on match
  let timedPref = false; // asked for ten-minute chats on the landing (HELLO { timed: true })
  // The explicit lane (adults, operator switch on). Chosen in ONE place -- the
  // in-chat settings popover -- because it is not advertised anywhere on the
  // site, least of all the landing, which minors see. Remembered per TAB, like
  // the age proof: it should not outlive the visit on a shared device.
  let explicitAvailable = false; // this session may have it at all (from WELCOME)
  let explicitPref = false; // which lane to be matched into next
  // What it takes to mint a SECOND session token for this visit. A token lives
  // two minutes, so the one the page started with is long dead by the time a
  // connection drops -- a reconnect has to ask for a new one, and these are the
  // answers it would otherwise have to ask the person for again.
  let resumeSeed = null; // { birthYear, effectiveMode } once a session is open
  let reconnecting = false; // one attempt loop at a time, however many closes arrive

  try {
    explicitPref = sessionStorage.getItem('lumi.explicit') === '1';
  } catch {
    /* no storage: start in the ordinary lane */
  }
  function setExplicitPref(on) {
    explicitPref = on === true;
    try {
      sessionStorage.setItem('lumi.explicit', explicitPref ? '1' : '0');
    } catch {
      /* non-fatal */
    }
    signaling?.send(ClientMsg.SET_EXPLICIT, { on: explicitPref });
    chatApi?.setExplicitLane?.({ available: explicitAvailable, on: explicitPref });
  }
  let reveal = { me: false, them: false, both: false }; // the pair's camera reveal, per match
  let producingVideo = false; // a camera produce is in flight (REVEAL_STATE and start() can race)
  let explicitAgreed = false; // MY half of the consent in the current pair
  let slowDownTimer = null; // retry READY after a slow_down (the per-hour cap on minors)

  function applyCosmetics() {
    const s = account.settings || {};
    document.documentElement.dataset.theme = s.theme || '';
    document.documentElement.dataset.orb = s.orbSkin || '';
    document.documentElement.dataset.plus = account.isPlus ? '1' : '';
  }

  // Site-wide announcement banner, set by the admin (Settings tab). Fetched at
  // startup and every 2 minutes; a changed message reappears even if the user
  // dismissed an earlier one (dismissal is remembered per exact text).
  async function refreshAnnouncement() {
    let text = '';
    try {
      const r = await fetch(`${SERVER_HTTP}/api/announcement`);
      ({ text } = await r.json());
    } catch {
      return; // transient -- keep whatever is showing
    }
    let dismissedFor = '';
    try {
      dismissedFor = sessionStorage.getItem('lumi.announce.dismissed') || '';
    } catch {
      /* storage unavailable */
    }
    const existing = document.querySelector('.announce-banner');
    if (!text || text === dismissedFor) {
      existing?.remove();
      return;
    }
    if (existing) {
      if (existing.dataset.text !== text) {
        existing.dataset.text = text;
        existing.querySelector('span').textContent = text;
      }
      return;
    }
    const banner = el('div', { class: 'announce-banner', role: 'status', dataset: { text } }, [
      el('span', { text }),
      el('button', {
        class: 'announce-close',
        'aria-label': 'Dismiss announcement',
        text: 'x',
        onClick: () => {
          try {
            sessionStorage.setItem('lumi.announce.dismissed', banner.dataset.text || '');
          } catch {
            /* not remembered, still dismissed */
          }
          banner.remove();
        },
      }),
    ]);
    document.body.append(banner);
  }
  refreshAnnouncement();
  setInterval(refreshAnnouncement, 120000);

  // Grab the accused's CURRENT video frame for a report. In 1:1 that is the one
  // remote video; in a group room it is that member's tile. Returns a base64
  // JPEG, or null when there is nothing on screen (audio/text chat, camera off).
  // This is the reporter's own view, so treat it as their claim -- evidence for
  // a human to weigh, never grounds for an automated action.
  function grabPeerFrame(peerId) {
    try {
      const v = peerId
        ? document.querySelector(`.tile[data-peer="${peerId}"] video`)
        : document.getElementById('remoteVideo');
      if (!v || !v.videoWidth || !v.videoHeight) return null;
      const c = document.createElement('canvas');
      c.width = v.videoWidth;
      c.height = v.videoHeight;
      c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
      return c.toDataURL('image/jpeg', 0.9).split(',')[1] || null;
    } catch {
      return null; // a report must never fail because the snapshot did
    }
  }

  // ---- Admin drop-in: a consent-first live session with an administrator ----
  let dropInPc = null;
  let dropInMedia = null;
  function stopDropIn() {
    try { dropInPc?.close(); } catch { /* closed */ }
    dropInPc = null;
    dropInMedia?.getTracks().forEach((track) => track.stop());
    dropInMedia = null;
    document.querySelector('.dropin-overlay')?.remove();
  }
  function showAdminDropIn(m) {
    if (!m || !m.offer) return;
    stopDropIn(); // a new request replaces any previous one
    // mode describes what the ADMIN sends: 'video' means they appear on camera
    // too, 'audio' means you hear their voice only. Either way you join with
    // camera and microphone, which the consent text states plainly.
    const adminOnCamera = m.mode !== 'audio';
    const status = el('div', {
      class: 'dropin-status',
      text: adminOnCamera
        ? 'An administrator would like to video chat with you. They will see and hear you, and you will see them.'
        : 'An administrator would like to talk with you. They will see and hear you; you will hear their voice.',
    });
    const remote = el('video', { autoplay: '', playsinline: '', class: 'dropin-remote hidden' });
    const self = el('video', { autoplay: '', playsinline: '', muted: '', class: 'dropin-self hidden' });
    const startBtn = el('button', { class: 'btn btn-primary btn-sm', type: 'button', text: t('Start') });
    const endBtn = el('button', { class: 'btn btn-ghost btn-sm', type: 'button', text: t('Not now'), onClick: stopDropIn });
    const overlay = el('div', { class: 'dropin-overlay' }, [
      el('div', { class: 'dropin-card' }, [
        el('div', { class: 'dropin-title', text: t('Lumi admin') }),
        remote,
        self,
        status,
        el('div', { class: 'dropin-actions' }, [startBtn, endBtn]),
      ]),
    ]);
    startBtn.addEventListener('click', async () => {
      startBtn.disabled = true;
      status.textContent = 'Starting your camera...';
      try {
        // Camera + mic in BOTH modes -- the admin always sees you; the mode
        // only decides whether you see them.
        try {
          dropInMedia = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: true });
        } catch {
          dropInMedia = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
        }
        const cfg = m.ice ? { iceServers: m.ice.iceServers, iceTransportPolicy: m.ice.iceTransportPolicy } : {};
        dropInPc = new RTCPeerConnection(cfg);
        for (const track of dropInMedia.getTracks()) dropInPc.addTrack(track, dropInMedia);
        dropInPc.ontrack = (e) => {
          if (!e.streams[0]) return;
          // The element also plays the admin's VOICE, so it stays in the DOM
          // either way; it is only shown once their video actually arrives.
          remote.srcObject = e.streams[0];
          if (e.streams[0].getVideoTracks().length) remote.classList.remove('hidden');
        };
        dropInPc.onconnectionstatechange = () => {
          if (['failed', 'disconnected', 'closed'].includes(dropInPc?.connectionState)) stopDropIn();
        };
        await dropInPc.setRemoteDescription(JSON.parse(m.offer));
        const answer = await dropInPc.createAnswer();
        await dropInPc.setLocalDescription(answer);
        await new Promise((resolve) => {
          if (dropInPc.iceGatheringState === 'complete') { resolve(); return; }
          const iceTimer = setTimeout(resolve, 4000);
          dropInPc.addEventListener('icegatheringstatechange', () => {
            if (dropInPc.iceGatheringState === 'complete') { clearTimeout(iceTimer); resolve(); }
          });
        });
        signaling?.send(ClientMsg.ADMIN_LIVE_ANSWER, { answer: JSON.stringify(dropInPc.localDescription) });
        self.srcObject = dropInMedia;
        self.classList.remove('hidden');
        startBtn.classList.add('hidden');
        endBtn.textContent = 'End';
        const hasMic = (dropInMedia.getAudioTracks?.() || []).length > 0;
        status.textContent = hasMic
          ? 'Live with the admin: they can see and hear you.'
          : 'Live with the admin: they can see you (your mic is blocked).';
      } catch (err) {
         
        console.warn('admin drop-in failed', err);
        stopDropIn();
        toast(
          err && err.name === 'NotAllowedError'
            ? 'Your camera and microphone are blocked for this site: allow them in your browser settings to talk with the admin.'
            : `Could not start the chat with the admin (${(err && err.name) || 'error'}).`,
        );
      }
    });
    document.body.append(overlay);
  }

  // A celebratory, energetic banner shown when an admin grants the user Lumi Plus. It
  // acknowledges the notice (so it shows once), auto-dismisses, and animates in.
  function showPlusBanner() {
    if (document.querySelector('.plus-banner')) return;
    const banner = el('div', { class: 'plus-banner' }, [
      el('div', { class: 'plus-banner-glow' }),
      el('div', { class: 'plus-banner-body' }, [
        el('div', { class: 'plus-banner-title', text: t('You have been given Lumi Plus!') }),
        el('div', {
          class: 'plus-banner-sub',
          text: t('Priority matching, higher video quality, and exclusive cosmetics are now yours. Enjoy!'),
        }),
      ]),
      el('button', { class: 'plus-banner-close', text: t('Nice!'), onClick: dismiss }),
    ]);
    let dismissed = false;
    function dismiss() {
      if (dismissed) return;
      dismissed = true;
      banner.classList.add('out');
      setTimeout(() => banner.remove(), 450);
      account.ackNotice().catch(() => {});
    }
    document.body.append(banner);
    requestAnimationFrame(() => banner.classList.add('in'));
    setTimeout(() => banner.isConnected && dismiss(), 9000);
  }

  function checkNotice() {
    if (account.notice && account.notice.type === 'plus_granted') showPlusBanner();
  }

  // Near-live: a logged-in user gets the banner when they refocus the tab or on a gentle
  // poll, even if the grant happened while they were mid-chat.
  function startNoticeWatch() {
    const tick = async () => {
      if (!account.token) return;
      await account.refresh().catch(() => {});
      applyCosmetics();
      checkNotice();
    };
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) tick();
    });
    setInterval(tick, 60000);
  }

  function openPlus() {
    openBilling(account, {
      onChange: () => {
        applyCosmetics();
        checkNotice(); // a fresh sign-in / claim may carry an admin-granted Plus notice
        syncAccountLink();
      },
    });
  }

  /** The shop, opened at the promotion section for one room. */
  function openPromo(roomCode) {
    openBilling(account, {
      promoRoom: roomCode,
      onChange: () => {
        applyCosmetics();
        checkNotice();
        syncAccountLink();
      },
    });
  }

  // Keep the topbar's account button honest without re-rendering the landing,
  // which would discard a half-typed name, birth year, or interests.
  // The text-only layout hands the whole screen to the chat while there is no
  // video, and gives it back the moment a camera comes on -- either camera.
  // Evaluated from the live streams rather than from the chosen mode, because a
  // text chat where someone later enables video must not stay text-shaped.
  let remoteHasVideo = false;
  // How long the "Say hi to Bea!" greeting stays up before taking itself away.
  // Long enough to read a name, short enough that it is never the thing you are
  // staring at while you wait to see somebody.
  const GREETING_MS = 3000;

  function refreshTextOnlyLayout() {
    if (!chatApi) return;
    const mine = (localStream?.getVideoTracks?.() || []).some((track) => track.enabled && track.readyState === 'live');
    chatApi.setVideoActive(mine || remoteHasVideo);
  }

  function syncAccountLink() {
    const link = root.querySelector('#accountLink');
    if (!link) return;
    link.textContent = account.username ? `@${account.username}` : t('Sign in');
    link.title = account.username ? 'Your account' : 'Sign in or create an account';
  }

  // The landing usually renders before the account fetch returns (and a person
  // can sign in later without leaving it), so the account name is offered IN
  // PLACE -- re-rendering would discard anything typed in the meantime. Only
  // when the field is untouched AND they have never made a choice (no stored
  // key at all, not even an empty one, which records "I want to be anonymous").
  function offerAccountName() {
    if (!account.username) return;
    let chosen = null;
    try {
      chosen = localStorage.getItem('lumi.handle');
    } catch {
      /* storage unavailable */
    }
    const input = root.querySelector('#handle');
    if (!input || input.value || chosen !== null) return;
    input.value = account.username;
    const hint = root.querySelector('#handleHint');
    if (hint) {
      hint.textContent =
        'This is your account name. Clear it to stay anonymous: it is only shown if you leave it here.';
    }
  }

  /**
   * Person auth for the HTTP routes that know who you are across visits: the
   * account token when signed in, the keepsake key when there is one, both
   * when both (the server prefers the account). Empty means nobody to ask about.
   */
  function personHeaders() {
    const headers = {};
    if (account.token) headers.Authorization = `Bearer ${account.token}`;
    const ks = getKeepsake();
    if (ks) headers['X-Lumi-Keepsake'] = ks.key;
    return headers;
  }

  function openAccountPanel() {
    openAccount(account, {
      onPlus: openPlus,
      // The door to the regulars panel: shown when the feature is on for this
      // person (a live socket said so), the landing found they have some, or
      // they are signed in (an account holder can start a community there).
      onRegulars: social || landingRegularsCount > 0 || account.loggedIn ? openRegularsPanel : null,
      regularsCount: regularsState.regulars.length || landingRegularsCount,
      onChange: () => {
        applyCosmetics();
        checkNotice();
        syncAccountLink();
        offerAccountName();
      },
    });
  }

  // ---- Regulars ----
  // One bell per screen that has a header; all of them show the same count.
  function makeBell() {
    const bell = createBell({ onOpen: openRegularsPanel });
    bells.add(bell.api);
    bell.api.setUnread(unreadNotices);
    return bell.root;
  }

  /**
   * Put a bell in the screen that is up, if this band has regulars at all.
   *
   * Called twice on purpose. The 1:1 chat screen is built BEFORE the socket
   * connects, so at render time nothing yet knows whether the wave exists --
   * `social` arrives with WELCOME, a moment later. Asking once at render meant
   * the answer was always "no" and the bell never appeared for anybody.
   * Idempotent: a slot that already holds a bell is left alone.
   */
  function ensureBell() {
    if (!social) return;
    const slot = chatApi?.bellSlot;
    if (slot && !slot.firstChild) slot.append(makeBell());
  }

  function syncBells() {
    for (const b of bells) b.setUnread(unreadNotices);
  }

  /** Walk into the room a notice or a regular's row named. By id, else by code. */
  function joinFromAround(around) {
    if (!signaling || !around) return;
    intent = 'group';
    if (around.roomId) signaling.send(ClientMsg.JOIN_ROOM, { roomId: around.roomId });
    else if (around.code) signaling.send(ClientMsg.JOIN_ROOM, { code: around.code });
  }

  /**
   * Enter a community's room. With a socket, the message; without one (the
   * landing), the code is kept and the next start opens it -- the same path a
   * /c/CODE link takes.
   */
  function openCommunity(code) {
    if (!code) return;
    if (signaling) {
      intent = 'group';
      regularsPanel?.close?.();
      signaling.send(ClientMsg.COMMUNITY_JOIN, { code });
      return;
    }
    communityCode = String(code).toUpperCase();
    regularsPanel?.close?.();
    toast('Pick your birth year and press Group rooms: your community opens from there.');
  }

  function openRegularsPanel() {
    unreadNotices = 0;
    syncBells();
    regularsPanel?.close?.();
    // The community card lives in this panel. Built once per opening and
    // handed over as a node, so the panel's own redraws (each REGULARS) move
    // it rather than rebuilding a half-typed session form.
    communityCard = renderCommunityCard({
      client: communityClient,
      connected: () => !!signaling,
      regulars: () => regularsState.regulars,
      onOpen: openCommunity,
      // The hub behind this panel draws the same community from its own copy
      // of /mine. Starting one here used to leave "Start a community" sitting
      // on the hub until a reload.
      onChanged: () => communitySection?.api.refresh(),
    });
    const opened = openRegulars({
      state: regularsState,
      // "Connected" means the panel has live data to show and something to do
      // with it. That used to require a socket, which made the panel useless
      // from the home page -- the one place you go to wave at somebody days
      // later. It is now true whenever we have the list, however it arrived.
      connected: (!!signaling && social) || (regularsState.recent || []).length > 0 || (regularsState.regulars || []).length > 0,
      count: landingRegularsCount,
      onBlock: (personId) => signaling?.send(ClientMsg.REGULAR_BLOCK, { personId }),
      onJoin: joinFromAround,
      onCall: callsOn ? startCall : null, // calls (5.B4)
      canVideo: videoAllowed,
      onInvisible: (invisible) => signaling?.send(ClientMsg.PRESENCE_SET, { invisible }),
      // Quiet hours ride the same message; the server reads them off the same
      // prefs row and gates every notice on them.
      onQuietHours: (quietHours) => signaling?.send(ClientMsg.PRESENCE_SET, { quietHours }),
      onRefresh: () => signaling?.send(ClientMsg.REGULARS_LIST),
      // A wave sent later, from the list of people you talked to this week.
      // Same wave, same seven days the site has always promised -- just not
      // only in the twenty seconds after the chat ended.
      onWavePerson: (personId) => {
        if (signaling) signaling.send(ClientMsg.WAVE, { person: personId });
        else waveOverHttp(personId);
      },
      communityCard: communityCard.root,
      onClose: () => {
        regularsPanel = null;
        // A call just ended and you looked at your regulars; closing the
        // panel without ringing anyone means back to finding someone new.
        if (afterCall) {
          afterCall = false;
          if (signaling && inOneOnOne() && !pairedNow && !callingSheet) signaling.send(ClientMsg.READY);
        }
        communityCard = null;
      },
      // Messages between regulars (ui/messages.js): the socket calls the
      // thread view makes. Null when the switch is off for this band, and the
      // rows simply have no Message button.
      messages:
        signaling && social && messagesOn
          ? {
              list: (withPersonId, before) => signaling?.send(ClientMsg.MSG_LIST, before ? { with: withPersonId, before } : { with: withPersonId }),
              send: (to, text, id, slow) => signaling?.send(ClientMsg.MSG_SEND, { to, text, id, slow: !!slow }),
              remove: (id) => signaling?.send(ClientMsg.MSG_DELETE, { id }),
              report: (id, reason) => signaling?.send(ClientMsg.MSG_REPORT, { id, reason }),
              newId: newMessageId,
            }
          : null,
    });
    regularsPanel = opened.api;
    // A fresh list, so the panel is not showing a stale "around".
    if (signaling && social) signaling.send(ClientMsg.REGULARS_LIST);
  }

  // ---- Calls (5.B4) ----------------------------------------------------------

  /**
   * Ring a regular. The sheet goes up at once; the server's first CALL_STATE
   * (ringing) names the call, and every later one moves the sheet along.
   * The server decides everything else, and says so in CALL_STATE or ERROR.
   */
  function startCall(personId, mode, handle) {
    if (!signaling || !callsOn) return;
    callingSheet?.close();
    callingSheet = showCallingSheet({
      handle,
      mode,
      onCancel: (callId) => {
        if (callId) signaling?.send(ClientMsg.CALL_CANCEL, { callId });
      },
      onClose: ({ connecting }) => {
        callingSheet = null;
        // A call that came to nothing leaves us idle on the 1:1 screen: the
        // server held us out of the queue while it rang, and puts us back
        // only if that is where we were. Asking again is harmless if it did.
        if (!connecting && signaling && inOneOnOne() && !pairedNow && !inCall) signaling.send(ClientMsg.READY);
      },
    });
    signaling.send(ClientMsg.CALL_INVITE, { personId, mode });
  }

  /** Answer an offer, or let the sheet answer for us (a timeout, a match arriving). */
  function respondToCall(callId, accept) {
    signaling?.send(ClientMsg.CALL_RESPOND, { callId, accept });
  }

  /**
   * A call ended, from either side. Not a search: you are back with your
   * regulars -- the panel opens over the chat, or over the rooms hub if that
   * is where you came from -- and the search resumes only when you close it
   * without ringing anyone (see openRegularsPanel). No Wave: you already are.
   */
  function endCallUi() {
    inCall = false;
    pairedNow = false;
    teardownPeer();
    resetPairUi();
    setCallChrome(root, null);
    toast('Call ended');
    if (intent === 'group') {
      // Back to the rooms you were browsing; the 1:1 screen the call drew is gone.
      chatApi = null;
      enterRoomsHub();
      openRegularsPanel();
      return;
    }
    chatApi?.setSkipEnabled(false);
    chatApi?.setStatus('Call ended', 'Your regulars are a tap away. Close the list to find someone new.');
    openRegularsPanel();
    afterCall = true; // after the open, so a panel that was already up does not close into a search
  }

  /**
   * The chat just ended: offer the wave, for this pair only. `reason` is the
   * server's word on how it ended; a report is never wavable, and the server
   * would refuse anyway. Nothing is offered when the feature is off for this
   * band (WELCOME.social) -- the controls simply do not exist for a minor.
   */
  function offerWave({ pairId, canWave = true, reason = null, line } = {}) {
    if (!chatApi || !pairId) return;
    const ok = social && canWave && reason !== 'report';
    chatApi.showEnded?.({
      pairId,
      canWave: ok,
      onWave: ok ? (id) => signaling?.send(ClientMsg.WAVE, { pairId: id }) : null,
      line,
    });
  }

  /**
   * The landing has no socket, so it asks over HTTP how many regulars this
   * person has, and shows "Regulars (N)" only when N > 0. Person auth is the
   * account token when signed in, else the keepsake key; with neither there
   * is nobody to ask about.
   */
  async function fetchRegularsCount() {
    const headers = {};
    if (account.token) headers.Authorization = `Bearer ${account.token}`;
    const ks = getKeepsake();
    if (ks) headers['X-Lumi-Keepsake'] = ks.key;
    if (!Object.keys(headers).length) return 0;
    try {
      const r = await fetch(`${SERVER_HTTP}/api/regulars/count`, { headers });
      if (!r.ok) return 0;
      const body = await r.json();
      return body.social ? Number(body.count) || 0 : 0;
    } catch {
      return 0;
    }
  }

  /**
   * The panel's data without a socket.
   *
   * It used to arrive only over the chat socket, so the panel worked only from
   * inside a chat -- which is the one place waving LATER is no use. The whole
   * point of the seven-day window is coming back to it from the home page.
   */
  async function fetchRegularsList() {
    const headers = personHeaders();
    if (!Object.keys(headers).length) return null;
    try {
      const r = await fetch(`${SERVER_HTTP}/api/regulars/list`, { headers });
      if (!r.ok) return null;
      const body = await r.json();
      return body.social ? body : null;
    } catch {
      return null;
    }
  }

  /** A wave with no socket to send it down. Same rules, server-side. */
  async function waveOverHttp(personId) {
    const headers = { ...personHeaders(), 'Content-Type': 'application/json' };
    try {
      const r = await fetch(`${SERVER_HTTP}/api/regulars/wave`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ person: personId }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body.ok) {
        toast('That wave could not be sent.');
        return;
      }
      toast(body.mutual ? 'You are now regulars.' : 'Waved.');
      const next = await fetchRegularsList();
      if (next) {
        regularsState = {
          regulars: next.regulars || [],
          invisible: !!next.invisible,
          quietHours: next.quietHours || null,
          recent: next.recent || [],
        };
        regularsPanel?.update(regularsState);
      }
    } catch {
      toast('That wave could not be sent.');
    }
  }

  function showRegularsLink() {
    const link = root.querySelector('#regularsLink');
    if (!link) return;
    // Somebody with no regulars YET but a chat they can still wave at needs
    // this door too -- it is the only way back to that person, and hiding it
    // was why a seven-day window may as well have been twenty seconds.
    const waveable = (regularsState.recent || []).length;
    if (landingRegularsCount > 0) {
      link.textContent = t('Regulars ({n})', { n: landingRegularsCount });
      link.hidden = false;
    } else if (waveable > 0) {
      link.textContent = t('Regulars');
      link.title = t('People you talked to this week, and anyone you have both waved at');
      link.hidden = false;
    } else {
      link.hidden = true;
    }
  }

  // A group invite link looks like https://host/r/CODE (also accept ?room=CODE and
  // #/r/CODE) so people can be linked straight to a room -- they verify their age first,
  // then auto-join that room.
  /**
   * A 1:1 invite from the URL: /i/CODE, ?chat=CODE, or #/i/CODE.
   *
   * Its own parser rather than a flavour of the room one: the codes have
   * different alphabets and confusing a room code for a person's code would
   * send somebody to the wrong place entirely.
   */
  function parseDirectInvite() {
    try {
      const m = location.pathname.match(/\/i\/([A-Za-z0-9]{8})\/?$/);
      if (m) return m[1].toUpperCase();
      const q = new URLSearchParams(location.search).get('chat');
      if (q && /^[A-Za-z0-9]{8}$/.test(q)) return q.toUpperCase();
      const h = (location.hash || '').match(/^#\/?i\/([A-Za-z0-9]{8})$/);
      if (h) return h[1].toUpperCase();
    } catch {
      /* ignore */
    }
    return null;
  }

  function parseInviteCode() {
    try {
      const m = location.pathname.match(/\/r\/([A-Za-z0-9]{4,12})\/?$/);
      if (m) return m[1].toUpperCase();
      const q = new URLSearchParams(location.search).get('room');
      if (q && /^[A-Za-z0-9]{4,12}$/.test(q)) return q.toUpperCase();
      const h = (location.hash || '').match(/^#\/?r\/([A-Za-z0-9]{4,12})$/);
      if (h) return h[1].toUpperCase();
    } catch {
      /* ignore */
    }
    return null;
  }
  joinCode = parseInviteCode();
  arrivingInvite = joinCode ? null : parseDirectInvite();

  // What the Lumi Link points at: the room's name and KIND, asked for before
  // there is a session so the landing can preselect Text for a text room
  // and never ask for a camera on the way in. Best effort and bounded: with
  // no answer the landing is the ordinary invite, which is what it was.
  let inviteRoom = null;
  async function peekRoom(code) {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 1500);
    try {
      const res = await fetch(`${SERVER_HTTP}/api/rooms/peek?code=${encodeURIComponent(code)}`, { signal: ctl.signal });
      if (!res.ok) return null;
      const body = await res.json();
      return body && body.ok && typeof body.mode === 'string' ? { name: String(body.name || ''), mode: body.mode } : null;
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }
  if (joinCode) {
    peekRoom(joinCode).then((room) => {
      inviteRoom = room;
      // The landing was drawn before the answer came. Draw it again with the
      // room known, unless the person has already started typing into it.
      const landing = root.querySelector('.landing');
      const typing = landing && landing.contains(document.activeElement) && /^(INPUT|SELECT|TEXTAREA)$/.test(document.activeElement?.tagName || '');
      if (room && landing && !typing) showLanding();
    });
  }

  // A community invite link looks like https://host/c/CODE (also ?community=CODE
  // and #/c/CODE). It does not join anything: the person verifies their age,
  // sees the community's name and size, and asks. See landOnCommunity.
  function parseCommunityCode() {
    try {
      const m = location.pathname.match(/\/c\/([A-Za-z0-9]{4,12})\/?$/);
      if (m) return m[1].toUpperCase();
      const q = new URLSearchParams(location.search).get('community');
      if (q && /^[A-Za-z0-9]{4,12}$/.test(q)) return q.toUpperCase();
      const h = (location.hash || '').match(/^#\/?c\/([A-Za-z0-9]{4,12})$/);
      if (h) return h[1].toUpperCase();
    } catch {
      /* ignore */
    }
    return null;
  }
  communityCode = joinCode ? null : parseCommunityCode();

  /**
   * Arrived by a /c/CODE link (or pressed Open before there was a socket):
   * ask the server where this person stands. A member or the owner walks in;
   * anyone else sees the landing with one button, and the plain words about
   * what asking does. Never a member list.
   */
  async function landOnCommunity(code) {
    communityCode = null;
    try {
      if (/\/c\//.test(location.pathname)) history.replaceState({}, '', '/');
    } catch {
      /* ignore */
    }
    if (!signaling) return;
    const r = await communityClient.byCode(code);
    if (r.status !== 200) {
      toast(r.status === 404 ? 'That community link does not work.' : 'Communities are not available for you yet.');
      return;
    }
    const info = r.data;
    if (info.you === 'member' || info.you === 'owner') {
      openCommunity(code);
      return;
    }
    openCommunityLanding({
      code,
      info,
      onAsk: () => communityClient.request(code, handle || undefined),
      onAccept: () => communityClient.accept(info.id),
      onOpen: () => openCommunity(code),
    });
  }

  function showLanding() {
    cleanup();
    mount(
      root,
      renderLanding({
        onStart: start,
        onPlus: openPlus,
        onAccount: openAccountPanel,
        onRegulars: openRegularsPanel,
        invite: joinCode ? { code: joinCode, room: inviteRoom } : communityCode ? { code: communityCode, kind: 'community' } : null,
        // Signed in -> offer their account name, which they can clear.
        accountName: account.username,
      }),
    );
    // The account pill, from live state rather than from whatever was known
    // when this render was queued. renderLanding is handed accountName, but
    // at boot the landing and the account arrive in either order: the sync
    // inside initAccount runs against a landing that may not be mounted yet,
    // and then the pill said "Sign in" to somebody who was signed in for the
    // rest of the visit. Syncing here means every path that draws the landing
    // corrects it, including the redraw when a Lumi Link resolves its room.
    syncAccountLink();
    // "Regulars (N)" in the top bar, once the server says N > 0.
    showRegularsLink();
    fetchRegularsCount().then((n) => {
      landingRegularsCount = n;
      showRegularsLink();
    });
    // And the list itself, so "Regulars" opens on to something from here --
    // including the people you talked to this week and can still wave at.
    fetchRegularsList().then((body) => {
      if (!body) return;
      regularsState = {
        regulars: body.regulars || [],
        invisible: !!body.invisible,
        quietHours: body.quietHours || null,
        recent: body.recent || [],
      };
      regularsPanel?.update(regularsState);
      showRegularsLink();
    });
    // Admin chat (support): reveal the entry point only when the admin has
    // enabled the feature in Settings.
    fetch(`${SERVER_HTTP}/api/support/enabled`)
      .then((r) => r.json())
      .then(({ enabled }) => {
        if (!enabled) return;
        const legal = root.querySelector('.landing-inner .legal');
        if (!legal || root.querySelector('.support-entry')) return;
        legal.before(
          el('div', { class: 'support-entry-row' }, [
            el('button', { class: 'btn btn-ghost btn-sm support-entry', text: t('Chat with an Admin'), onClick: showSupport }),
          ]),
        );
      })
      .catch(() => {});
  }

  function showSupport() {
    cleanup();
    let savedHandle = '';
    try {
      savedHandle = localStorage.getItem('lumi.handle') || '';
    } catch {
      /* fine */
    }
    mount(root, renderSupport({ onBack: showLanding, handle: savedHandle }).root);
  }

  // The selfie age check, shown full-screen. The shared verifyAge() handles capture,
  // submission, review polling, AND storing the resulting token so it is reused.
  async function runAgeVerification(birthYear, fingerprint, who = {}) {
    let cancelled = false;
    let refusals = 0; // photos the server refused (retake, liveness, image) -- not network hiccups
    const verify = renderVerify({
      onCancel: () => {
        cancelled = true;
        showLanding();
      },
    });
    mount(root, verify.root);
    // Loop so a not-good-enough photo (or a slow/failed check) shows a "Try again" on
    // THIS screen instead of leaving the user stuck on "Checking..." forever. The
    // finally guarantees a live-check camera/mic never outlives this screen.
    try {
    for (;;) {
      try {
         
        return await verifyAge(birthYear, fingerprint, {
          onVideo: (s) => verify.api.setVideo(s),
          onStatus: (statusTitle, sub) => {
            verify.api.setStatus(statusTitle, sub);
            if (statusTitle === 'Checking...' || /review queue/.test(statusTitle)) verify.api.showSpinner(true);
          },
          // Human review: a moderator can message the waiting user and read
          // their reply right on this screen -- or run a brief live camera check.
          onChatReady: (send) => verify.api.enableChat(send),
          onMessages: (msgs) => verify.api.setChatMessages(msgs),
          onLiveCheck: (req) => verify.api.offerLiveCheck(req),
          handle: who.handle, // shown to the reviewer with the selfie
          accountToken: who.accountToken,
          isCancelled: () => cancelled,
          // A human after ONE refusal. This is the client's count; the server
          // keeps its own (age-funnel.js), so a page reload between tries does
          // not put anyone back at the start of the loop. Counted on refusals
          // only: two network hiccups are not two bad photos.
          escalate: refusals >= 1,
        });
      } catch (err) {
        if (cancelled || /cancelled/.test(String(err?.message))) throw err;
        verify.api.showSpinner(false);
        const retryable = !!err?.retry;
         
        const choice = await verify.api.showError(
          err?.message || 'Something went wrong. Please try again.',
          { retry: retryable },
        );
        if (choice === 'retry' && retryable) {
          if (err?.refusal) refusals += 1;
          continue;
        }
        showLanding(); // message already shown; return to the start
        throw Object.assign(new Error('cancelled'), { handled: true });
      }
    }
    } finally {
      verify.api.stopLive?.();
    }
  }

  // Render the 1:1 chat screen. Used at the start of a 1:1 session AND when a group
  // member transitions to a private 1:1 (the "go private" feature).
  function showOneOnOneChat() {
    teardownPeer();
    roomsApi = null;
    groupApi?.dispose?.(); // let the room take its window listeners with it
    groupApi = null;
    currentRoom = null;
    const chat = renderChat({
      // Read when a message is drawn, so it is the band of THIS session.
      get externalLinks() {
        return myBand === 'adult';
      },
      onStop: showLanding,
      onSkip: () => signaling?.send(ClientMsg.SKIP),
      onGoBack: () => signaling?.send(ClientMsg.GOBACK),
      onReport: (reason, image) => {
        // Attach what is being complained about: a reported FILE sends that
        // file's own still, otherwise the reporter's current view of the peer.
        // Without either, a video report reaches the queue with nothing to see.
        signaling?.send(ClientMsg.REPORT, { reason, image: image || grabPeerFrame() });
        toast('Thanks for looking out. We are on it.');
      },
      onSend: (text, replyTo) => {
        // While staff have a text drop-in open there is no partner to relay to
        // -- being unpaired is why they are talking to you -- so what you type
        // goes to them instead of nowhere.
        if (adminChatOpen) {
          signaling?.send(ClientMsg.ADMIN_CHAT_SAY, { text });
          chatApi?.addText('me', text, { id: newMessageId() });
          return;
        }
        // The id is minted HERE and shown on the local copy too, so both sides
        // name the same message when replying, reacting, or unsending.
        const id = newMessageId();
        signaling?.send(ClientMsg.CHAT, { text, id, replyTo: replyTo || null });
        chatApi?.addText('me', text, { id, replyTo });
      },
      onReact: (id, emoji, on) => signaling?.send(ClientMsg.REACT, { id, emoji, on }),
      onUnsend: (id) => signaling?.send(ClientMsg.UNSEND, { id }),
      onTyping: (state) => signaling?.send(ClientMsg.TYPING, { state }),
      onFile: (file) => sendFile(file),
      onMicVolume: (gain) => {
        if (micGain) micGain.gain.value = gain;
      },
      canVideo: videoAllowed,
      onEnableMic: () => enableMedia({ audio: true }),
      onEnableCam: async () => {
        await enableMedia({ video: true });
        refreshTextOnlyLayout();
      },
      onCamToggle: () => refreshTextOnlyLayout(),
      onReveal: () => requestReveal(), // "Show my camera" (camera-off start)
      // Agree to explicit content with this person, or stop it. The same button
      // either way: stopping must never be harder than starting, and must be
      // one tap at any moment.
      onExplicit: () => {
        explicitAgreed = !explicitAgreed;
        signaling?.send(ClientMsg.EXPLICIT, { on: explicitAgreed });
      },
      onExplicitLane: (on) => setExplicitPref(on),
      onEffect,
      onVideoPin: (url) => signaling?.send(ClientMsg.VIDEO_PIN, { url }),
      onVideoControl: (action, atMs) => signaling?.send(ClientMsg.VIDEO_CONTROL, { action, atMs }),
      canModerate: isModerator,
      onKick: (reason, note) => signaling?.send(ClientMsg.KICK, { reason, note }),
    });
    chatApi = chat.api;
    ensureBell();
    // Show yourself what your partner sees on you.
    chatApi.setSelfBadges({ staff: isStaff, moderator: isModerator, plus: isPlusMember });
    mount(root, chat.root);
    chatApi.setLocalStream(localStream);
    refreshTextOnlyLayout();
    refreshSelfView(); // show any saved blur/filter in the user's own preview too
    chatApi.initEffects(effectPrefs);
    refreshSelfView(); // if an effect was already on, show the processed video immediately
    // The explicit lane, if this session may have it at all.
    //
    // This used to be drawn only on MATCHED, so the control that decides WHO
    // YOU MEET NEXT first appeared after you had already met somebody -- and
    // never at all for a person sitting in the queue, which is exactly where
    // you would go looking for it. Drawn with the screen now; setExplicitLane
    // removes its own row when the session is not eligible, so a minor and a
    // site with the switch off still see nothing.
    chatApi.setExplicitLane?.({ available: explicitAvailable, on: explicitPref });
  }

  async function start(opts) {
    try {
      return await startInner(opts);
    } catch (err) {
      if (err?.handled || /cancelled/.test(String(err?.message || ''))) throw err;
      // A failure AFTER the landing was replaced (e.g. the verify screen took
      // over during an age check) used to write its message onto the detached
      // old landing -- invisible, so the user saw a frozen "review queue".
      // Remount the landing and show the error where the user actually is.
      showLanding();
      const line = root.querySelector('.gate .error');
      if (line) line.textContent = err?.message || 'Something went wrong. Please try again.';
      throw Object.assign(err instanceof Error ? err : new Error(String(err)), { handled: true });
    }
  }

  async function startInner({
    birthYear,
    mode,
    intent: intentArg,
    handle: handleArg,
    interest: interestArg,
    cameraStart: cameraStartArg,
    timed: timedArg,
  }) {
    intent = joinCode || communityCode || intentArg === 'group' ? 'group' : '1on1';
    chosenMode = mode || chosenMode;
    handle = handleArg || null;
    interest = interestArg || null;
    cameraStart = cameraStartArg === 'open' ? 'open' : 'agree';
    timedPref = timedArg === true;
    // The last certain user gesture before somebody sits waiting with the tab
    // in the background. A browser will not start an AudioContext without one,
    // and every tone we might need is played long after this point.
    primeAudio();
    const fingerprint = await getFingerprint();

    // 1. Get a session, running the self-hosted age check if the server asks for it.
    // Reuse a stored age proof (this device, within the token's two-hour life)
    // so the user is not re-prompted; only run the selfie check when there is
    // no valid proof yet.
    let session = await obtainSession(birthYear, fingerprint, getStoredAge()?.token, account.token);
    if (session.status === 'verification_required') {
      const ageToken = await runAgeVerification(birthYear, fingerprint, {
        handle: handleArg || handle || undefined,
        accountToken: account.token || undefined,
      });
      session = await obtainSession(birthYear, fingerprint, ageToken, account.token);
    }
    if (session.status !== 'ok') throw new Error('Verification did not complete. Please try again.');
    const { token, policy } = session;
    myBand = session.band === 'minor' ? 'minor' : session.band === 'adult' ? 'adult' : null;

    // Apply the minor-pool policy: minors do not get unmoderated live video.
    let effectiveMode = mode;
    if (mode === ChatMode.VIDEO && policy && policy.videoAllowed === false) {
      effectiveMode = ChatMode.AUDIO;
      toast('Video chat for under-18 is moderated and coming soon: connecting by audio.');
    }
    // Whether this user may later turn the camera on mid-chat (gated by policy).
    videoAllowed = !(policy && policy.videoAllowed === false);
    // Everything a reconnect needs that is not already module state. The age
    // proof and the account token are read from storage at the time, so they
    // are deliberately NOT captured here: a proof that expired in the meantime
    // must send the person back through the check, not be replayed.
    resumeSeed = { birthYear, effectiveMode };

    // 2. Capture local media for the chosen mode. Coming straight from an age
    // check, the camera was released only milliseconds ago (selfie burst or a
    // live check) and phones often have not freed the hardware yet -- so a
    // busy-camera failure retries with a pause instead of giving up.
    const acquireMedia = async (constraints) => {
      let lastErr;
      for (let attempt = 0; attempt < 4; attempt += 1) {
        try {
           
          return await navigator.mediaDevices.getUserMedia(constraints);
        } catch (err) {
          lastErr = err;
          if (err && (err.name === 'NotReadableError' || err.name === 'AbortError')) {
             
            await new Promise((r) => setTimeout(r, 600));
            continue;
          }
          throw err;
        }
      }
      throw lastErr;
    };
    try {
      if (effectiveMode === ChatMode.VIDEO) {
        // facingMode 'user': on phones a bare { video: true } can open the
        // REAR camera. Soft preference here (desktop webcams have no facing).
        try {
          localStream = await acquireMedia({ video: { facingMode: 'user' }, audio: true });
        } catch {
          // A blocked mic must not kill a VIDEO chat: fall back to camera-only
          // (the user can still type) instead of failing the combined request.
          localStream = await acquireMedia({ video: { facingMode: 'user' } });
          toast('Your microphone is blocked, so you are connecting without sound: you can still type.');
        }
      } else if (effectiveMode === ChatMode.AUDIO) {
        localStream = await acquireMedia({ audio: true });
      } else {
        localStream = null;
      }
    } catch (err) {
      // Name exactly what is missing for the mode we actually connect in (a
      // minor's video choice downgrades to audio, which needs only the mic),
      // and give a way OUT: browsers never re-prompt once access is blocked,
      // so without guidance the user is stuck in a retry loop.
      const what = effectiveMode === ChatMode.VIDEO ? 'camera and microphone' : 'microphone';
      if (err && err.name === 'NotAllowedError') {
        throw new Error(
          `Your ${what} is blocked for this site. Tap the lock icon next to the address bar, allow the ${what}, and try again: or switch to Text chat.`,
        );
      }
      throw new Error(
        `Lumi could not start your ${what} (${(err && err.name) || 'unknown error'}). Close other apps that may be using it and try again, or switch to Text chat.`,
      );
    }

    // Route the mic through a gain node so its volume is adjustable, and send THAT
    // (processed) audio to the peer. The raw localStream stays the self-view.
    sendStream = localStream;
    audioCtx = null;
    micGain = null;
    audioEnhancer = null;
    videoEnhancer = null;
    rawCamTrack = (localStream && localStream.getVideoTracks()[0]) || null;
    if (localStream && localStream.getAudioTracks().length > 0) {
      try {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const src = audioCtx.createMediaStreamSource(new MediaStream(localStream.getAudioTracks()));
        micGain = audioCtx.createGain();
        const dest = audioCtx.createMediaStreamDestination();
        // Noise suppression (#9) -- bypassed unless the user has it on.
        audioEnhancer = createAudioEnhancer(audioCtx, src);
        audioEnhancer.setEnabled(!!effectPrefs.denoise);
        audioEnhancer.output.connect(micGain).connect(dest);
        audioCtx.resume?.();
        // A context created behind the camera-permission prompt starts SUSPENDED (the
        // original click is stale by the time getUserMedia resolves), which silences the
        // sent audio and leaves the mic meter + mic-volume gain dead. Resume it on the
        // user's next interaction until it is actually running.
        armAudioContextResume();
        sendStream = new MediaStream([
          ...localStream.getVideoTracks(),
          ...dest.stream.getAudioTracks(),
        ]);
      } catch {
        sendStream = localStream; // Web Audio unavailable -- send the raw mic
      }
    }
    // Background blur (#8) / filters (#11): route the SENT video through the canvas
    // processor when an effect is on (the self-view stays the raw camera).
    if (rawCamTrack && videoFxOn()) {
      if (effectPrefs.blur) ensureMlSegmenter(); // saved pref -> start loading now
      try {
        videoEnhancer = createVideoEnhancer(rawCamTrack, { segmenter: bgSegmenter });
        videoEnhancer.setBlur(!!effectPrefs.blur);
        videoEnhancer.setFilter(effectPrefs.filter);
        await videoEnhancer.start();
        sendStream = new MediaStream([videoEnhancer.track, ...sendStream.getAudioTracks()]);
      } catch {
        videoEnhancer = null;
      }
    }
    // Saved prefs (blur/filter from a previous session) applied only to what the
    // PEER saw; the self-view still showed the raw camera, so the effect looked
    // dead. refreshSelfView() below runs once the chat screen exists.

    // 3. Render the 1:1 chat screen now; group rooms get their own UI after WELCOME.
    if (intent !== 'group') {
      showOneOnOneChat();
      chatApi.setStatus('Connecting...', 'Getting you ready to chat.');
    }

    // 4. Connect signaling.
    signaling = new Signaling();
    wireSignaling();
    try {
      await signaling.connect(token);
    } catch {
      showLanding();
      throw new Error('Could not connect to Lumi. Please try again.');
    }
    // Preferred language for translated captions (e.g. "en", "es") from the browser.
    const lang = (navigator.language || '').slice(0, 2).toLowerCase() || null;
    signaling.send(ClientMsg.HELLO, {
      mode: effectiveMode,
      handle,
      lang,
      interest,
      timed: timedPref,
      explicit: explicitPref,
      // Used ONCE, at the moment of joining: the server either pairs us with
      // the person whose link this is, or says why not.
      inviteCode: arrivingInvite || undefined,
    });
  }

  /**
   * Open a fresh socket for a session already under way, and pick up where the
   * server left off.
   *
   * The reason this is not simply `signaling.connect(token)` again: a session
   * token lives two minutes. After a ten-minute conversation the page is
   * holding an expired one, and the server would answer 401. So a reconnect
   * mints a NEW token from the same things the first one was minted from --
   * the stored age proof, the account token, the device fingerprint -- none of
   * which asks the person for anything. If the age proof has expired in the
   * meantime, obtainSession says verification is required, and that is a
   * question only the landing can ask: we give up and go there rather than
   * launching a camera at somebody who was in the middle of a chat.
   *
   * Backoff is jittered because the common cause is the SERVER going away --
   * a deploy, or the nightly security upgrade restarting everything -- which
   * means every client on the site is doing this at the same moment. In step
   * they would arrive as one burst and be turned away by the per-address
   * socket cap; spread out, they trickle back.
   */
  const RECONNECT_DELAYS_MS = [400, 1200, 2500, 5000, 9000];
  async function reconnect() {
    if (reconnecting || !signaling || !resumeSeed) return;
    reconnecting = true;
    try {
      for (let attempt = 0; attempt < RECONNECT_DELAYS_MS.length; attempt += 1) {
        const base = RECONNECT_DELAYS_MS[attempt];
        await new Promise((r) => setTimeout(r, base + Math.random() * base));
        if (!signaling || !reconnecting) {
          // The person left, or something else took over, while we waited.
          reconnecting = false;
          return;
        }
        try {
          const fingerprint = await getFingerprint();
          const session = await obtainSession(
            resumeSeed.birthYear,
            fingerprint,
            getStoredAge()?.token,
            account.token,
          );
          if (session.status !== 'ok') break; // needs the age check again: the landing's job
          await signaling.connect(session.token);
          const lang = (navigator.language || '').slice(0, 2).toLowerCase() || null;
          // The same greeting the first connection sent. WELCOME then puts the
          // person back where they belong on its own: into the queue for a 1:1,
          // or on the rooms list for somebody who was in a group.
          signaling.send(ClientMsg.HELLO, {
            mode: resumeSeed.effectiveMode,
            handle,
            lang,
            interest,
            timed: timedPref,
            explicit: explicitPref,
          });
          reconnecting = false;
          return;
        } catch {
          /* try again after the next, longer wait */
        }
      }
    } finally {
      if (reconnecting) {
        // Out of attempts. This is the old behaviour, and the right one: the
        // page cannot do anything useful without a server.
        reconnecting = false;
        toast('Lost connection to Lumi. Please start again.');
        showLanding();
      }
    }
  }

  function wireSignaling() {
    signaling.on(ServerMsg.WELCOME, (m) => {
      selfId = m.selfId;
      selfBand = m.band || 'adult'; // decides whether "open from the start" is honored
      rememberBand(selfBand); // so the next landing hides what a minor is never offered
      // Adults only, and only while the operator's switch is on. A minor's
      // WELCOME never carries this, so their client cannot show the option.
      explicitAvailable = !!m.explicit;
      if (!explicitAvailable) explicitPref = false;
      // WELCOME is what SAYS whether this session may have the lane, and it
      // usually lands after the chat screen is already up. Draw the control
      // the moment we know, rather than waiting for a first match -- which is
      // what hid it from everybody sitting in the queue.
      chatApi?.setExplicitLane?.({ available: explicitAvailable, on: explicitPref });
      shareKey = m.shareKey || null; // capability for uploading/fetching files
      sfuEnabled = !!m.sfu;
      maxVideoBitrate = m.maxVideoBitrate || 0; // Plus -> higher ceiling
      isModerator = !!m.moderator; // admin-granted: can kick peers for 15 min
      // Your OWN badges. Everyone else could see these on you; you could not,
      // so there was no way to confirm your staff badge was actually live.
      isStaff = !!m.staff;
      isPlusMember = !!m.plus;
      invitesAllowed = !!m.invites; // may this band hand out a 1:1 invite link
      moderateImages = !!m.moderateImages; // sample camera frames for image moderation
      asrEnabled = !!m.asr; // sample audio for transcription (moderation + captions)
      iceConfig = { iceServers: m.iceServers, iceTransportPolicy: m.iceTransportPolicy };
      // Whether the wave exists for this band (decision H1: adults first). The
      // list is asked for once here so the bell has something to open on.
      social = !!m.social;
      callsOn = social && !!m.calls; // and whether a regular can be called from here
      messagesOn = !!m.messages;
      if (social) signaling.send(ClientMsg.REGULARS_LIST);
      // Now that we know, give the screen already on its bell.
      ensureBell();
      if (intent === 'group') {
        // Invited via a /r/CODE link -> join that room straight away; a /c/CODE
        // link -> the hub, with the community's landing over it; otherwise the hub.
        if (joinCode) signaling.send(ClientMsg.JOIN_ROOM, { code: joinCode });
        else if (communityCode) {
          const code = communityCode;
          enterRoomsHub();
          landOnCommunity(code);
        } else enterRoomsHub();
      } else {
        signaling.send(ClientMsg.READY);
        // The server says whether this band may hand one out; asking when
        // it may not simply comes back null, but not asking is tidier.
        if (invitesAllowed) signaling.send(ClientMsg.INVITE_MINE);
      }
    });

    // ---- Group rooms ----
    // The room's shared video, in full on every change.
    // Stable so the notice can be taken back when the video is unpinned.
    const VIDEO_NOTICE_ID = 'room-video-pinned';
    // The room's shared video. The FIRST one after joining is announced in the
    // chat as well as shown in the panel: a room is ephemeral, so a newcomer
    // sees none of the conversation that led to it and would otherwise have to
    // work out for themselves why a video appeared.
    signaling.on(ServerMsg.ROOM_VIDEO, (m) => {
      const first = !sawVideoState;
      sawVideoState = true;
      // The same message serves a group room and a 1:1 -- whichever the
      // person is actually in.
      if (groupApi) groupApi.setVideo(m);
      else chatApi?.setVideo?.(m);
      if (!m.videoId) {
        // The notice describes a state, not an event: "this room has a video
        // pinned" stops being true the moment it is unpinned, and leaving it
        // there tells people something false about the room they are in.
        groupApi?.removeSystem?.(VIDEO_NOTICE_ID);
        sawVideoState = false; // so the next video announces itself again
        return;
      }
      if (first) {
        // A SoundCloud track is not a video, and calling it one reads as a
        // mistake to the person looking straight at a waveform.
        const audio = videoProvider(m.provider)?.kind === 'audio';
        groupApi?.addSystem(
          m.playing
            ? (audio
              ? 'Listening together. It is already playing, and you are in sync with the room.'
              : 'Watching together. It is already playing, and you are in sync with the room.')
            : (audio
              ? 'Something is pinned to listen to together.'
              : 'A video is pinned to watch together.'),
          { id: VIDEO_NOTICE_ID },
        );
      }
    });
    // A card for a link somebody sent. Arrives after the message, since
    // fetching it takes a moment.
    signaling.on(ServerMsg.LINK_PREVIEW, (m) => {
      if (groupApi) groupApi.linkPreview(m.id, m);
      else chatApi?.linkPreview(m.id, m);
    });
    // Ownership moved -- the owner left and it passed to whoever has been here
    // longest. Without this the controls would stay with somebody who is gone.
    signaling.on(ServerMsg.ROOM_OWNER, (m) => {
      if (currentRoom) currentRoom.ownerId = m.ownerId;
      groupApi?.setVideoOwner(m.ownerId === selfId, m.ownerId);
      if (m.ownerId === selfId) groupApi?.addSystem('You are now the room host.');
    });
    // The watch-together queue (D7), in full on every change and on join. The
    // screen needs to know which entries are mine and whether the turn is mine.
    signaling.on(ServerMsg.ROOM_VIDEO_QUEUE, (m) => groupApi?.setVideoQueue?.(m, selfId));
    // Activities (D0): the state in full, and relayed events, both handed to
    // the frame by the group screen. slug null means none is running.
    signaling.on(ServerMsg.ACTIVITY_STATE, (m) => groupApi?.setActivity?.(m));
    signaling.on(ServerMsg.ACTIVITY_EVENT, (m) => groupApi?.activityEvent?.(m));

    signaling.on(ServerMsg.ROOM_LIST, (m) => {
      // The staff listing (every band, private rooms included) is a different
      // list from the public directory and must not overwrite it.
      if (m.all) roomsApi?.setAllRooms(m.rooms || []);
      else roomsApi?.setRooms(m.rooms || []);
    });
    signaling.on(ServerMsg.ROOM_JOINED, (m) => enterGroupRoom(m));
    signaling.on(ServerMsg.ROOM_PEER_LEFT, (m) => {
      roomHandles.delete(m.peerId);
      mutedPeers.delete(m.peerId);
      sfuPeer?.removePeer(m.peerId);
      // Drop their tile too. Without this the member outlives their departure:
      // somebody who left and rejoined appeared TWICE, and the "N here" count
      // kept counting the ghost.
      groupApi?.removePeer(m.peerId);
    });
    signaling.on(ServerMsg.ROOM_VIDEO_SLOTS, (m) => {
      videoSlots = m;
      groupApi?.setVideoSlots(slotsForUi(m));
      // Waiting for a camera slot and one just opened: take it. The camera is
      // on; this is what the person was waiting for.
      if (videoWaiting && m.used < m.max) takeVideoSlot();
    });
    signaling.on(ServerMsg.ROOM_PEER_JOINED, (m) => {
      if (m.handle) roomHandles.set(m.peerId, m.handle);
      if (m.peerId && m.peerId !== selfId) {
        groupApi?.ensurePeer(m.peerId, m.handle);
        if (m.moderator) groupApi?.setModerator(m.peerId, true);
        if (m.staff) groupApi?.setStaff(m.peerId, true); // verified Lumi staff
        if (m.plus) groupApi?.setPlus(m.peerId, true); // subtle Plus mark
        // Everyone arrives quietly listening; the count itself comes in
        // ROOM_OBSERVERS, this only marks the person in the roster.
        if (m.observing) groupApi?.setPeerObserving?.(m.peerId, true);
        if (m.status) groupApi?.setPeerStatus?.(m.peerId, m.status);
        // Someone arrived. The server sends this only to people ALREADY in the
        // room, so a joiner never hears their own entry, and never one tone per
        // member they walked in on -- that list arrives as ROOM_JOINED instead.
        playJoinTone();
      }
    });
    signaling.on(ServerMsg.ROOM_CHAT, (m) => {
      // Speaking ends listening. The server's count arrives separately; this
      // only clears the roster mark on the person who spoke.
      groupApi?.setPeerObserving?.(m.from, false);
      if (mutedPeers.has(m.from)) return; // muted: drop their texts
      // peerId travels with the message so the sender's badges can be drawn
      // beside their name -- in a text room that is the only place identity
      // is visible at all.
      groupApi?.addMessage(m.handle || String(m.from).slice(0, 4), m.text, {
        id: m.id,
        replyTo: m.replyTo,
        peerId: m.from,
        // Badges now travel WITH the message. They used to be looked up from
        // the join announcement, which an invisible staff member never sends --
        // so their messages arrived unbadged. Carrying them here is sturdier
        // anyway: a missed join no longer means an unbadged staff member.
        flags: { staff: m.staff, moderator: m.moderator, plus: m.plus },
      });
      // A room carries on talking whether or not you are looking at it, and the
      // line you missed may be an answer to something you asked.
      noteMessage();
    });
    signaling.on(ServerMsg.ROOM_ERROR, (m) => {
      // A community's code, tried by somebody who is not in it: the landing,
      // where they can ask, rather than a dead end.
      if (m.error === 'members_only' && m.code) {
        joinCode = null;
        if (!currentRoom && !roomsApi) enterRoomsHub();
        landOnCommunity(m.code);
        return;
      }
      toast(roomErrorText(m.error));
      // Ejected from a community room (removed, or the community closed), or
      // staff closed the room outright: the room is gone from under us, so
      // back to the hub. Without this last one a person turned out of a closed
      // room sat looking at a room that no longer exists, able to type into it.
      if (
        (m.error === 'removed_from_community' || m.error === 'community_closed' || m.error === 'room_closed') &&
        currentRoom
      ) {
        enterRoomsHub();
        return;
      }
      // A bad/expired invite link should not strand the user -- drop into the hub.
      if (joinCode && !currentRoom) {
        joinCode = null;
        enterRoomsHub();
      }
    });
    // The community a room belongs to, in full, on join and on every change.
    signaling.on(ServerMsg.COMMUNITY_STATE, (m) => {
      communityState = m;
      if (currentRoom && currentRoom.community === m.id) {
        currentRoom.name = m.name;
        groupApi?.setRoomName?.(m.name);
        communityPanel?.update(m);
      }
      communityCard?.api.refresh();
    });

    // ---- Going private (from a group room) ----
    signaling.on(ServerMsg.PRIVATE_REQUEST, (m) => {
      // A muted person cannot reach me with a 1:1 request -- auto-decline silently.
      if (mutedPeers.has(m.fromId)) {
        signaling?.send(ClientMsg.GO_PRIVATE_RESPOND, { fromId: m.fromId, accept: false });
        return;
      }
      groupApi?.askPrivate?.(m.fromHandle || 'Someone', m.fromId);
    });
    /**
     * "A room would like you to join them for a few minutes."
     *
     * Deliberately a plain question with a plain no. Nobody is told who
     * declined, declining costs nothing and does not move you in the queue, and
     * it answers itself after thirty seconds so an unanswered invitation cannot
     * sit around waiting to be pressed by accident later.
     */
    const askStageOffer = (m) => {
      document.querySelector('.stage-offer')?.remove();
      let answered = false;
      const answer = (accept) => {
        if (answered) return;
        answered = true;
        clearTimeout(timer);
        box.remove();
        signaling?.send(ClientMsg.STAGE_RESPOND, { accept });
      };
      const people = Number(m.people) || 0;
      const box = el('div', { class: 'stage-offer' }, [
        el('p', {
          class: 'stage-offer-title',
          text: `${m.roomName || 'A room'} would like you to join them`,
        }),
        el('p', {
          class: 'stage-offer-body',
          text: `${people} ${people === 1 ? 'person is' : 'people are'} in there. `
            + `You would be a guest for ${Math.round((Number(m.seconds) || 600) / 60)} minutes, `
            + 'and you can leave at any point.',
        }),
        el('div', { class: 'stage-offer-actions' }, [
          el('button', { class: 'btn btn-primary btn-sm', text: t('Join them'), onClick: () => answer(true) }),
          el('button', { class: 'btn btn-ghost btn-sm', text: t('No thanks'), onClick: () => answer(false) }),
        ]),
      ]);
      document.body.append(box);
      const timer = setTimeout(() => answer(false), 30_000);
    };

    // Somebody's room would like to invite you in. This has to work from
    // wherever you are -- you are in the waiting queue, not in a room -- so
    // it is its own small prompt rather than anything a room owns.
    signaling.on(ServerMsg.STAGE_OFFER, (m) => askStageOffer(m));
    signaling.on(ServerMsg.STAGE_STATE, (m) =>
      groupApi?.setStage?.({ ...m, isMe: !!m.guestId && m.guestId === selfId }));
    signaling.on(ServerMsg.STAGE_ENDED, () => {
      toast('Your ten minutes are up. Thanks for dropping in.');
    });
    signaling.on(ServerMsg.PRIVATE_DECLINED, (m) => toast(`${m.byHandle || 'They'} declined to go private.`));
    signaling.on(ServerMsg.ROOM_VOTE_STARTED, (m) => groupApi?.showVote?.(m));
    signaling.on(ServerMsg.ROOM_VOTE_UPDATE, (m) => groupApi?.updateVote?.(m));
    signaling.on(ServerMsg.ROOM_VOTE_RESULT, (m) => {
      groupApi?.endVote?.(m);
      toast(m.passed ? 'The room is now private: no new random members.' : 'The vote did not pass.');
    });

    // ---- Pinned rules, the quiet room, and observer mode ----
    // The pinned card, re-sent on the hour when the question rotates.
    signaling.on(ServerMsg.ROOM_PIN, (m) => groupApi?.setPin?.(m));
    // How many are here and have not yet spoken. The server counts; the
    // client only draws.
    signaling.on(ServerMsg.ROOM_OBSERVERS, (m) => groupApi?.setWatching?.(m.watching));
    // The quiet room's shared timer, in full on every change.
    signaling.on(ServerMsg.ROOM_FOCUS, (m) => groupApi?.setFocus?.(m));
    // A member's one-line status. Yours comes back too, so the field shows
    // what actually got through the moderator.
    signaling.on(ServerMsg.ROOM_PEER_STATUS, (m) =>
      groupApi?.setPeerStatus?.(m.peerId === selfId ? 'self' : m.peerId, m.text));

    signaling.on(ServerMsg.SEARCHING, () => {
      teardownPeer();
      pairedNow = false;
      afterCall = false;
      resetPairUi(); // nothing of the last pair -- clock, reveal, card -- stays on screen
      chatApi?.setStatus('Finding someone new...', 'Hang tight.');
      armQuietHint();
      armInviteOffer();
    });

    signaling.on(ServerMsg.MATCHED, (m) => {
      // Coming from a group room (went private)? Render the 1:1 chat first.
      if (!chatApi) showOneOnOneChat();
      // A match is a pair, call or not: an offer sheet still up is answered
      // no (the server would refuse the accept anyway), and a "Calling..."
      // sheet that is not this call is taken down.
      pairedNow = true;
      afterCall = false;
      if (offerSheet) {
        const id = offerSheet.callId;
        offerSheet.close();
        respondToCall(id, false);
      }
      if (callingSheet && !m.call) {
        const id = callingSheet.callId;
        callingSheet.close();
        if (id) signaling?.send(ClientMsg.CALL_CANCEL, { callId: id });
      }
      inCall = !!m.call;
      // The pair a wave will be for, and no Wave control from the last one.
      lastPairId = m.pairId || null;
      chatApi?.clearWave?.();
      chatApi?.clearMessages();
      chatApi?.clearGoBack();
      chatApi?.setSkipEnabled(false);
      // The greeting is a NOTICE, not a state: it says who you got, and then it
      // gets out of the way. It used to be dismissed only by a remote stream
      // arriving or the peer connection reaching 'connected' -- and a pair with
      // no cameras between them produces neither, so "Connecting you..." sat
      // over a working conversation for its entire length. Text rides the
      // signalling socket, which is already up: the moment you are matched you
      // can talk, and saying so is the honest thing to put there. Video is the
      // only thing with anything left to connect, and the reveal placeholder
      // explains that better than an overlay can.
      cancelQuietHint();
      cancelInviteOffer();
      // The whole point of telling somebody to leave the tab open: when the
      // wait ends, the tab has to say so. Sounds and badges the title only
      // when they are actually looking elsewhere -- if they are watching the
      // screen, the chat appearing IS the notification.
      noteMatched();
      const videoComing = !!m.reveal?.both || hasLocalVideo();
      const connectingLine = videoComing ? 'Connecting you...' : 'You are connected. Say hello.';
      if (inCall) chatApi?.setStatus(`On a call with ${m.peerHandle || 'your regular'}`, connectingLine, GREETING_MS);
      else chatApi?.setStatus(m.peerHandle ? `Say hi to ${m.peerHandle}!` : 'Say hi!', connectingLine, GREETING_MS);
      // The watermark names WHOEVER IS WATCHING -- you -- not the person on
      // screen. It is drawn on your screen over their video, so a recording
      // made here carries your name: that is what makes a leaked clip traceable
      // and is why it deters. Naming them instead labelled the victim in the
      // recording and identified the recorder not at all. Nothing can stop a
      // screen recording (/safety says so); this makes one answerable for.
      chatApi?.setWatermark(`Lumi - ${m.selfHandle || handle || selfId.slice(0, 6)}`);
      chatApi?.setPeerName(m.peerHandle || 'a stranger'); // visible for the whole chat
      chatApi?.setPartnerModerator(!!m.peerModerator); // unfakeable verified-mod badge
      chatApi?.setPartnerStaff(!!m.peerStaff); // verified Lumi staff -- conspicuous
      chatApi?.setPartnerPlus(!!m.peerPlus); // the subtle Plus mark, quiet by design
      // Watching something together is an adults-only feature, so the pin box
      // is only offered to a pair that may use it.
      chatApi?.setVideoAllowed?.(m.band === 'adult');
      // Lower-pressure starts. MATCHED says where the cameras stand from the
      // first moment: closed until both tap (the operator's switch on), or
      // open from the start (the switch off) -- without that, a pair the
      // server had already opened sat behind a "Show my camera" placeholder
      // nothing could dismiss. The header says what they would rather speak
      // and, for a ten-minute chat, how long is left. A remembered "open from
      // the start" sends our half of the reveal now -- for adults only,
      // whatever the browser remembers, because a minor never has that option.
      reveal = m.reveal
        ? { me: !!m.reveal.me, them: !!m.reveal.them, both: !!m.reveal.both }
        : { me: false, them: false, both: false };
      chatApi?.setPeerLang?.(m.peerLang || null);
      chatApi?.setTimed?.(m.timed ? { endsAt: m.timed, warnAt: null } : null);
      // Explicit consent is per pair and starts at nothing, whatever the last
      // pair had agreed to. The control is absent unless BOTH chose the lane.
      chatApi?.setExplicit?.(m.explicitAvailable ? { available: true, me: false, them: false, both: false } : null);
      chatApi?.setExplicitLane?.({ available: explicitAvailable, on: explicitPref });
      pushRevealUi();
      // A call: "Call with Bea" in the header, and Skip reads Hang up. Applied
      // after setPeerName so it is the last word on the title.
      setCallChrome(root, inCall ? m.peerHandle || 'your regular' : null);
      if (cameraStart === 'open' && selfBand !== 'minor' && hasLocalVideo()) requestReveal();
      if (sfuEnabled) setupSfuPeer();
      else setupPeer(m.role === 'caller');
    });

    signaling.on(ServerMsg.SIGNAL, (m) => handleSignal(m));
    signaling.on(ServerMsg.SFU, (m) => handleSfuMessage(m));
    signaling.on(ServerMsg.CHAT, (m) => {
      chatApi?.addText('them', m.text, { id: m.id, replyTo: m.replyTo });
      // Somebody answered while this tab was behind something else. The title
      // counts it and a short tone plays; coming back clears both.
      noteMessage();
    });
    // A reaction or an unsend from the other side. The server stamps `from`, so
    // these can only ever act on the sender's own message.
    signaling.on(ServerMsg.REACTION, (m) => {
      groupApi?.setPeerObserving?.(m.from, false); // a reaction is a first word too
      if (mutedPeers.has(m.from)) return;
      groupApi?.peerReaction(m.id, m.emoji, m.from, m.on);
      chatApi?.peerReaction(m.id, m.emoji, m.from, m.on);
    });
    signaling.on(ServerMsg.UNSENT, (m) => {
      groupApi?.peerUnsend(m.id);
      chatApi?.peerUnsend(m.id);
    });
    signaling.on(ServerMsg.SYSTEM, (m) => {
      // A notice about a message between regulars (a refused one, a thanks
      // for a report) belongs in the thread it is about, not in the chat.
      if (m.scope === 'messages') {
        if (!regularsPanel?.messages?.notice(m)) toast(m.text, 4000);
        return;
      }
      // Notices go through addSystem, never addMessage: system styling must
      // not be reachable by anything a person can type or call themselves.
      const opts = { level: m.level, id: m.id };
      if (groupApi) groupApi.addSystem(m.text, opts);
      else chatApi?.addSystem(m.text, opts);
    });
    // Live captions: a partner's transcribed speech (muted peers are not shown).
    signaling.on(ServerMsg.CAPTION, (m) => {
      if (mutedPeers.has(m.from)) return;
      const who = m.handle || (m.from ? String(m.from).slice(0, 4) : 'They');
      if (groupApi) groupApi.showCaption?.(who, m.text);
      else chatApi?.showCaption?.(who, m.text);
    });
    signaling.on(ServerMsg.TYPING, (m) => {
      // A group room names who; a 1:1 has only one other person, so it does not
      // need to.
      if (groupApi) groupApi.setTyping(m.from, m.handle || roomHandles.get(m.from), !!m.state);
      else chatApi?.setTyping(!!m.state);
    });

    // Admin drop-in: an administrator asks to talk live (e.g. while the user
    // waits in the queue). Consent-first -- nothing starts until Start is
    // tapped -- then camera+mic stream to the admin over a direct session.
    signaling.on(ServerMsg.ADMIN_LIVE_OFFER, (m) => showAdminDropIn(m));

    // A moderator's live look while you wait. The camera is already open for
    // the self-view; this sends it, and the microphone, to the server's SFU
    // for as long as the look lasts, and stops when told. It draws nothing:
    // oversight of a queue is there to see what actually goes on in it. An
    // age-safe video chat is monitored; the site does not announce each look.
    signaling.on(ServerMsg.QUEUE_MEDIA, (m) => {
      if (m.on) startQueueMedia();
      else stopQueueMedia();
    });

    // A TEXT drop-in from Lumi staff while you wait to be paired. No camera and
    // no consent prompt, because nothing is captured -- somebody is typing to
    // you, and you can answer or not. Rendered as a staff message rather than a
    // system notice: it IS a person, and it expects a reply.
    signaling.on(ServerMsg.ADMIN_CHAT, (m) => {
      if (m.open === false) {
        chatApi?.addSystem('Lumi Staff ended the conversation.');
        adminChatOpen = false;
        return;
      }
      adminChatOpen = true;
      if (m.text) chatApi?.addStaffMessage(m.text);
    });

    // A file cleared moderation and is waiting on the server.
    signaling.on(ServerMsg.FILE_SHARED, (m) => receiveSharedFile(m));

    signaling.on(ServerMsg.SKIP_UNLOCKED, () => chatApi?.setSkipEnabled(true));

    // Your own "come and talk to me" link. Arrives on request and again
    // unasked after a reconnect, so a page that came back still knows it.
    signaling.on(ServerMsg.INVITE_LINK, (m) => {
      myInviteCode = m.code || null;
      showInviteIfWaiting();
    });

    // You arrived through somebody's link. 'ok' needs nothing said: MATCHED
    // is already on its way. Everything else is a reason, and the person came
    // here expecting a specific human, so it is said in words.
    signaling.on(ServerMsg.INVITE_RESULT, (m) => {
      arrivingInvite = null;
      if (m.ok) return;
      // No reason means the server decided this one is not ours to explain:
      // an expired or unknown code, or anything that would give away who the
      // link belonged to. Say nothing and let them have an ordinary visit --
      // once a link dies it IS just a link to Lumi. See _refuseInvite.
      const said = {
        busy: 'They are already in a chat. You are in the queue, so try their Lumi Link again in a minute.',
        away: 'They are not here right now. You are in the queue, so you will still meet somebody.',
        self: 'That is your own Lumi Link. Share it with somebody else!',
      };
      if (said[m.reason]) toast(said[m.reason]);
    });

    // ---- Lower-pressure starts ----
    // Where the pair stands on cameras. When both have said yes, the camera
    // that has been waiting (captured, shown to you, sent to nobody) goes up.
    signaling.on(ServerMsg.REVEAL_STATE, (m) => {
      const wasThem = reveal.them;
      reveal = { me: !!m.me, them: !!m.them, both: !!m.both };
      pushRevealUi();
      // With no camera of your own the placeholder is not on screen (the
      // text-only layout hides the stage), so their readiness is said aloud.
      if (reveal.them && !wasThem && !reveal.me && !hasLocalVideo()) toast('They are ready to show their camera.');
      if (reveal.both) {
        ensureVideoProduced();
        startModSampler(); // the camera is shown now, so it is moderated now
      }
    });
    // Explicit consent in this pair. `both` false again is a stop, and takes
    // effect on the server the moment it is sent -- this only redraws.
    signaling.on(ServerMsg.EXPLICIT_STATE, (m) => {
      chatApi?.setExplicit?.({ available: true, me: !!m.me, them: !!m.them, both: !!m.both });
    });
    // One card after twenty seconds of silence. System-styled by construction
    // (see ui/chat.js): the words are the server's deck, never a person's.
    signaling.on(ServerMsg.ICEBREAKER, (m) => {
      if (groupApi) return; // a room pins its question instead
      chatApi?.showIcebreaker?.(m.text, () => signaling?.send(ClientMsg.ICEBREAKER_SKIP));
    });
    signaling.on(ServerMsg.TIMED_STATE, (m) => chatApi?.setTimed?.({ endsAt: m.endsAt, warnAt: m.warnAt }));
    // The gentle end. No go-back and no PEER_LEFT: the time was the agreement.
    // The server's SEARCHING follows and takes over the status overlay.
    signaling.on(ServerMsg.TIMED_END, (m) => {
      teardownPeer();
      pairedNow = false;
      offerWave({ pairId: m.pairId || lastPairId, canWave: !!m.canWave });
    });

    // They moved on (or were removed). The wave is offered for the chat that
    // just ended -- unless it ended in a report, which is never wavable.
    // A call ending is its own thing: no search, no wave (you are regulars).
    signaling.on(ServerMsg.PEER_LEFT, (m) => {
      if (inCall || m?.reason === 'call_ended') return endCallUi();
      teardownPeer();
      pairedNow = false;
      chatApi?.setStatus('Finding someone new...', 'Hang tight.');
      return offerWave({ pairId: lastPairId, reason: m?.reason || null, line: 'The chat ended.' });
    });

    // Their connection died, but the chat is being held open for them.
    //
    // This used to be PEER_LEFT: a dropped signal and a person walking out
    // looked identical, so a phone changing network ended the conversation and
    // put both of them back in a queue that rarely has anyone else in it. Now
    // the chat stands for a moment and this says so, rather than lying about
    // somebody having left.
    signaling.on(ServerMsg.PEER_DROPPED, (m) => {
      const seconds = Math.max(1, Number(m?.seconds) || 45);
      chatApi?.setStatus(
        'They dropped out',
        `Their connection went. Holding the chat for ${seconds} seconds in case they get back. You can skip if you would rather not wait.`,
      );
    });
    // They made it back. A MATCHED follows immediately and rebuilds the call.
    signaling.on(ServerMsg.PEER_RETURNED, () => {
      chatApi?.setStatus('They are back', 'Reconnecting...');
    });

    // You skipped. You can still wave: a skip is not a verdict.
    signaling.on(ServerMsg.GOBACK_OFFER, (m) => {
      pairedNow = false;
      const duration = Math.max(0, (m.until || 0) - Date.now());
      chatApi?.setStatus('Finding someone new...', 'Skipped by mistake?');
      chatApi?.offerGoBack(duration || 3000, () => signaling?.send(ClientMsg.GOBACK));
      offerWave({ pairId: lastPairId, reason: 'skip', line: 'You moved on.' });
    });
    signaling.on(ServerMsg.GOBACK_FAILED, () => {
      chatApi?.clearGoBack();
      toast('They already moved on.');
    });

    // ---- Regulars ----
    signaling.on(ServerMsg.WAVED, (m) => chatApi?.waveResult?.(m));
    signaling.on(ServerMsg.REGULARS, (m) => {
      regularsState = {
        regulars: Array.isArray(m.regulars) ? m.regulars : [],
        invisible: !!m.invisible,
        quietHours: m.quietHours || null,
        recent: Array.isArray(m.recent) ? m.recent : [],
      };
      landingRegularsCount = regularsState.regulars.length;
      regularsPanel?.update(regularsState);
    });
    // A notice: the bell's dot, a soft tone, and a toast. A "regular is around"
    // notice carries the room, so its toast has a Join.
    signaling.on(ServerMsg.NOTIFY, (m) => {
      // A call is a moment, not something the panel lists: no unread dot for
      // it. The toast ("Leave the room to pick up") is for a tab in a room;
      // anywhere else the ringing sheet IS the notice, so nothing doubles it.
      if (m.kind === 'call') {
        if (!groupApi) return;
        playNoticeTone();
        showNoticeToast(m);
        return;
      }
      // A message from the person whose thread is open on screen is already
      // on screen: no bell, no toast for it.
      if (m.kind === 'message' && m.personId && regularsPanel?.messages?.current() === m.personId) return;
      unreadNotices += 1;
      syncBells();
      playNoticeTone();
      // Somebody asked to join a community you own. The thing to do about it
      // is Accept, which lives in the panel -- not join the room, which is
      // yours already and answers nobody.
      if (m.kind === 'community_ask') {
        showNoticeToast(m, { onAction: () => openRegularsPanel(), actionLabel: 'Open' });
        return;
      }
      showNoticeToast(m, {
        onAction: m.roomId || m.code ? (n) => joinFromAround({ roomId: n.roomId, code: n.code }) : null,
      });
    });

    // ---- Calls (5.B4) ----
    // Somebody is ringing. Declined unseen when we are mid-chat or in a room:
    // the server did not offer it to such a session, but a client is not a
    // rule, and a sheet over a conversation would be the wrong surprise.
    signaling.on(ServerMsg.CALL_OFFER, (m) => {
      if (!m?.callId) return undefined;
      if (pairedNow || groupApi) return respondToCall(m.callId, false);
      offerSheet?.close();
      offerSheet = showOfferSheet({
        callId: m.callId,
        fromHandle: m.fromHandle || null,
        mode: m.mode,
        until: m.until,
        onRespond: respondToCall,
        onClose: () => {
          offerSheet = null;
        },
      });
      return undefined;
    });
    // Where a call stands. Matched to a sheet by callId: the ringing sheet
    // knows its call from the offer; the "Calling..." sheet learns its call
    // from the first of these. Anything else is a call we are no longer
    // showing, and is ignored.
    signaling.on(ServerMsg.CALL_STATE, (m) => {
      if (offerSheet && m.callId && offerSheet.callId === m.callId) {
        // cancelled, expired, connecting: the sheet goes; MATCHED follows a connecting.
        offerSheet.close();
        return;
      }
      callingSheet?.setState(m);
    });
    // ---- Messages between regulars (ui/messages.js) ----
    // A message for the open thread is appended there. Otherwise the row's
    // badge goes up; the NOTIFY that travels with it rings the bell.
    signaling.on(ServerMsg.MSG, (m) => {
      // Our own copy from another tab is not a message TO us: it must not
      // count, or writing on your phone would badge the tab on your desk. The
      // server puts clientId on the sender's copy and on no other, and sends it
      // to every one of the sender's tabs, so it is exactly this test.
      const mine = m.clientId != null;
      if (regularsPanel?.messages?.onMsg(m)) {
        if (!mine) noteMessage(); // the thread is open, but nobody is looking at it
        return;
      }
      const row = regularsState.regulars.find((r) => r.personId === m.from);
      if (!row) return; // our own copy from another tab, or nobody we list
      row.unread = (Number(row.unread) || 0) + 1;
      regularsPanel?.update(regularsState);
      noteMessage();
    });
    signaling.on(ServerMsg.MSG_LIST, (m) => {
      regularsPanel?.messages?.onList(m);
      // Listing marks the thread seen on the server; the row agrees at once.
      const row = regularsState.regulars.find((r) => r.personId === m.with);
      if (row && row.unread) {
        row.unread = 0;
        regularsPanel?.update(regularsState);
      }
    });
    signaling.on(ServerMsg.MSG_DELETED, (m) => regularsPanel?.messages?.onDeleted(m));

    signaling.on(ServerMsg.BANNED, (m) => {
      const mins = m.until ? Math.max(1, Math.round((m.until - Date.now()) / 60000)) : 0;
      toast(
        mins
          ? `A moderator removed you for ${mins} minute${mins === 1 ? '' : 's'} (${m.reason || 'rules violation'}).`
          : 'Access to Lumi has been restricted.',
        5000,
      );
      showLanding();
    });

    signaling.on(ServerMsg.ERROR, (m) => {
      // An open thread shows its own refusals (not regulars, the daily cap,
      // slow down) under the composer, where the person is looking.
      if (regularsPanel?.messages?.error(m)) return;
      if (m.code === 'rate_limited') toast('Slow down a moment.');
      // An invite refused for something about us: the sheet says so and goes.
      if (callingSheet) {
        const why = callErrorText(m.code);
        if (why) callingSheet.fail(why);
      }
      // The per-hour cap on new contacts (a minor in ten-minute mode). We are
      // not in the queue, so nothing happens until we ask again: say so, and
      // ask again in a minute. The server keeps saying no until the hour has room.
      if (m.code === 'slow_down') {
        chatApi?.setStatus('Take a breather', m.message || 'You have met a lot of people this hour. Come back in a bit.');
        scheduleReadyRetry();
      }
    });

    // The signaling socket is the ONLY thing that lets the server see, moderate,
    // or end a chat -- but WebRTC media keeps flowing without it. So losing the
    // socket (a server restart during a deploy, a dropped network) must end the
    // call on this side too, in a GROUP room just as much as in 1:1: otherwise
    // people keep talking in a session the server no longer knows exists.
    signaling.on('__close', () => {
      if (reconnecting) return; // already on it; a second close is the same news
      // A live chat ENDS here, always. That rule has not changed and must not:
      // WebRTC media keeps flowing without the socket, so a pair whose server
      // has forgotten them would otherwise keep talking in a session nothing is
      // moderating. The peer goes down before anything else happens.
      const wasChatting = !!(chatApi || groupApi);
      if (wasChatting) {
        teardownPeer(); // stops the samplers and the media; keeps the camera open
        pairedNow = false;
        inCall = false;
        resetPairUi();
      }
      // What HAS changed is where that leaves you. A dropped socket used to
      // mean the homepage, so a three-second library patch at 06:18 put
      // everybody on the site back to the front door. Now the page gets itself
      // a new connection and the server puts you back in the queue.
      if (chatApi) chatApi.setStatus('Reconnecting...', 'The connection dropped. Getting you back.');
      else if (wasChatting) toast('Connection lost. Reconnecting...');
      reconnect();
    });
  }

  // ---- SFU (mediasoup) media path -----------------------------------------

  /** Send one SFU request over the WS and resolve with the server's reply. */
  function sfuRpc(op, args = {}) {
    const reqId = (sfuReqId += 1);
    return new Promise((resolve, reject) => {
      sfuPending.set(reqId, { resolve, reject });
      signaling?.send(ClientMsg.SFU, { op, reqId, ...args });
      setTimeout(() => {
        if (sfuPending.has(reqId)) {
          sfuPending.delete(reqId);
          reject(new Error('sfu_timeout'));
        }
      }, 15000);
    });
  }

  /** Route an incoming SFU message: an RPC reply (has reqId) or a server event. */
  function handleSfuMessage(m) {
    if (m.reqId != null) {
      const pending = sfuPending.get(m.reqId);
      if (!pending) return;
      sfuPending.delete(m.reqId);
      if (m.ok) pending.resolve(m);
      else pending.reject(new Error(m.error || 'sfu_error'));
      return;
    }
    if (m.op === 'newProducer') sfuPeer?.onNewProducer(m.producerId, m.peerId);
    else if (m.op === 'producerClosed') sfuPeer?.onProducerClosed(m.producerId);
    else if (m.op === 'newDataProducer') sfuPeer?.onNewDataProducer(m.dataProducerId, m.peerId);
  }

  function setupSfuPeer() {
    teardownPeer();
    // Camera-off start is the SERVER's rule about who receives, not ours
    // about what to send: the camera goes up from the start, so a moderator
    // can watch any chat, and the partner is shown it only once both have
    // tapped "Show my camera". Frame moderation waits for that too (below).
    const full = sendStream || localStream;
    const initial = full;
    sfuPeer = new SfuPeer(
      (op, args) => sfuRpc(op, args),
      { localStream: initial, maxBitrate: maxVideoBitrate },
      {
        onPeerStream: (_peerId, stream) => {
          chatApi?.setRemoteStream(stream);
          remoteHasVideo = !!stream && (stream.getVideoTracks?.() || []).length > 0;
          refreshTextOnlyLayout();
          chatApi?.hideStatus();
          chatApi?.pulse();
          chatApi?.focusInput();
        },
        onPeerGone: () => {
          chatApi?.setRemoteStream(null);
          remoteHasVideo = false;
          refreshTextOnlyLayout();
        },
        onFile: (_peerId, { mime, blob, name }) =>
          chatApi?.addFile('them', { mime, url: URL.createObjectURL(blob), name }),
        onState: (state) => {
          if (state === 'connected') chatApi?.hideStatus();
        },
      },
    );
    sfuPeer
      .start()
      // Both may already have revealed by the time the transports exist.
      .then(() => ensureVideoProduced())
      .catch(() => toast('Could not connect the call. Finding someone new...'));
    // A camera nobody has opened is not moderated: in a 1:1 the sampler starts
    // when REVEAL_STATE says both (or now, if the pair was born revealed).
    if (!inOneOnOne() || reveal.both) startModSampler();
    startAudioSampler();
  }

  // ---- A moderator's look while waiting (ServerMsg.QUEUE_MEDIA) -----------
  // True while our media goes up for it. A pair or a room has its own SFU peer
  // and tears this one down on its way in (setupSfuPeer -> teardownPeer).
  let queueMediaOn = false;
  function startQueueMedia() {
    if (pairedNow || groupApi || !sfuEnabled) return; // a chat is already sending
    const full = sendStream || localStream;
    if (!full || !full.getTracks().length) return; // text mode: nothing to send
    teardownSfuPeer();
    queueMediaOn = true;
    // No callbacks: nothing comes back from a room whose only other peer is
    // recv-only, and nothing about this reaches the screen.
    sfuPeer = new SfuPeer((op, args) => sfuRpc(op, args), { localStream: full, maxBitrate: maxVideoBitrate }, {});
    sfuPeer.start().catch(() => {
      queueMediaOn = false;
    });
  }
  function stopQueueMedia() {
    if (!queueMediaOn) return;
    queueMediaOn = false;
    if (!pairedNow) teardownSfuPeer();
  }

  function teardownSfuPeer() {
    queueMediaOn = false;
    sfuPeer?.close();
    sfuPeer = null;
    for (const { reject } of sfuPending.values()) reject(new Error('closed'));
    sfuPending.clear();
  }

  // ---- Lower-pressure starts (client side) ---------------------------------

  /** In a 1:1, as opposed to a group room or the rooms hub. */
  function inOneOnOne() {
    return !!chatApi && !groupApi;
  }
  function hasLocalVideo() {
    return (localStream?.getVideoTracks?.() || []).some((track) => track.readyState === 'live');
  }
  /** Tell the chat screen where the pair stands on cameras. */
  function pushRevealUi() {
    if (!inOneOnOne()) return;
    chatApi?.setReveal?.({ ...reveal, hasVideo: hasLocalVideo() });
  }
  /** "Show my camera": our half of the agreement, the only half we can give. */
  function requestReveal() {
    if (!inOneOnOne() || reveal.me) return;
    signaling?.send(ClientMsg.REVEAL);
    // Shown at once; REVEAL_STATE confirms it and adds their side.
    reveal = { ...reveal, me: true };
    pushRevealUi();
  }
  /**
   * Make sure the camera is produced to the SFU, once. Called when the SFU
   * finishes starting and again when REVEAL_STATE says both, because a camera
   * can appear between the two. The reveal no longer gates this: the server
   * decides who receives the track.
   */
  async function ensureVideoProduced() {
    if (!inOneOnOne() || producingVideo) return;
    const track = outboundVideoTrack();
    if (!sfuPeer || !track || sfuPeer.hasProducer('video')) return;
    producingVideo = true;
    try {
      // null means no send transport yet; the call after start() produces.
      await sfuPeer.addTrack(track);
    } catch {
      toast('Could not start your camera. Please try again.');
    } finally {
      producingVideo = false;
    }
    refreshTextOnlyLayout();
  }
  /** A new search: nothing of the last pair stays on screen. */
  // Waiting, with nobody there yet.
  //
  // Lumi is new, and at a quiet hour a person can sit in the queue for
  // minutes. The honest advice is to stop staring at it: leave the tab open,
  // go and do something else, and let the tab shout. That only helps if we
  // actually say so, and only if we say it at the point where somebody has
  // started to wonder whether the site is broken -- not on arrival, which
  // would announce that nobody is here before they have waited at all.
  /**
   * When to offer the "come and talk to me" link.
   *
   * Thirty seconds. Later than the "leave this tab open" hint, because that
   * one is the cheaper thing to try first, and much earlier than the couple
   * of minutes it takes somebody to give up -- by which point they have gone
   * and the link has nobody to be shared by.
   */
  const INVITE_OFFER_AFTER_MS = 30_000;
  let inviteTimer = null;
  function showInviteIfWaiting() {
    if (!chatApi || pairedNow || !myInviteCode || !inviteOfferDue) return;
    chatApi.setInviteLink?.(`${location.origin}/i/${myInviteCode}`);
  }
  let inviteOfferDue = false;
  function armInviteOffer() {
    clearTimeout(inviteTimer);
    inviteTimer = setTimeout(() => {
      inviteTimer = null;
      inviteOfferDue = true;
      showInviteIfWaiting();
    }, INVITE_OFFER_AFTER_MS);
  }
  function cancelInviteOffer() {
    clearTimeout(inviteTimer);
    inviteTimer = null;
    inviteOfferDue = false;
    chatApi?.setInviteLink?.(null);
  }

  const QUIET_HINT_AFTER_MS = 18_000;
  let quietHintTimer = null;
  function armQuietHint() {
    clearTimeout(quietHintTimer);
    quietHintTimer = setTimeout(() => {
      quietHintTimer = null;
      if (!chatApi || pairedNow) return;
      chatApi.setStatus(
        'Still looking...',
        getJoinSoundOn()
          ? 'Quiet right now. Leave this tab open and go and do something else. You will hear a sound the moment someone arrives.'
          : 'Quiet right now. Leave this tab open and go and do something else. The tab title will say when someone arrives.',
      );
    }, QUIET_HINT_AFTER_MS);
  }
  function cancelQuietHint() {
    clearTimeout(quietHintTimer);
    quietHintTimer = null;
  }

  function resetPairUi() {
    reveal = { me: false, them: false, both: false };
    explicitAgreed = false;
    chatApi?.setExplicit?.(null);
    chatApi?.setReveal?.(null);
    chatApi?.setTimed?.(null);
    chatApi?.setPeerLang?.(null);
    // Who you were talking to goes when the PAIR goes -- not when the media
    // peer is rebuilt. Clearing it in teardownPeer meant every match wiped its
    // own name a few lines after setting it, because setupPeer/setupSfuPeer
    // begin by tearing the old peer down: the header read a bare "Chat" for
    // the whole conversation and nothing on screen said who this was.
    chatApi?.setPeerName?.(null);
    chatApi?.hideIcebreaker?.();
  }
  function scheduleReadyRetry() {
    clearTimeout(slowDownTimer);
    slowDownTimer = setTimeout(() => {
      slowDownTimer = null;
      if (signaling && inOneOnOne()) signaling.send(ClientMsg.READY);
    }, 60_000);
  }

  // ---- Group rooms (client) ----------------------------------------------

  function roomErrorText(code) {
    switch (code) {
      case 'room_not_found':
        return 'That room does not exist.';
      case 'room_full':
        return 'That room is full.';
      case 'code_required':
        return 'That is a private room: you need its code.';
      case 'band_mismatch':
        return 'That room is for a different age group.';
      // Community rooms. members_only normally carries the code and is routed
      // to the landing before it gets here; this is the wording without one.
      case 'members_only':
        return 'That is a community: only its members can enter.';
      case 'removed_from_community':
        return 'You are no longer a member of this community.';
      case 'community_closed':
        return 'This community has been closed.';
      // Staff ended the room from the console. The server sends the reason as
      // a system notice too, which is the part that says WHY; this is the
      // fallback wording for the toast, so it never reads "Could not join
      // that room" about a room you were sitting in.
      case 'room_closed':
        return 'Lumi Staff closed this room.';
      case 'bad_code':
        return 'That code does not look right.';
      // Inviting a guest fails with its own codes. They used to fall through to
      // the default below, so a failed invite said "Could not join that room" --
      // about a room you are already sitting in. That is worse than silence: it
      // sends you looking at the wrong thing.
      case 'no_room':
        return 'You need to be in a room to invite somebody.';
      case 'adults_only':
        return 'Guests can only be invited into adult rooms.';
      case 'owner_only':
        return 'Only the host can invite somebody in.';
      case 'already_guest':
        return 'There is already a guest here. Wait for their ten minutes to finish.';
      // Activities (D0) and the watch-together queue (D7).
      case 'activity_running':
        return 'Something is already running. Stop it first.';
      case 'not_enough_players':
        return 'That one needs more people in the room.';
      case 'host_only':
        return 'Only whoever is running the activity can do that.';
      case 'not_allowed':
        return 'That is not yours to change.';
      case 'not_available':
        return 'That is not available in this room.';
      case 'no_activity':
        return 'Nothing is running right now.';
      case 'too_large':
        return 'That was too big to send.';
      case 'bad_url':
        return 'That link is not one Lumi can play.';
      case 'queue_full':
        return 'The queue is full.';
      case 'queue_member_full':
        return 'You already have ten in the queue. Let somebody else add one.';
      case 'no_video':
        return 'Nothing is playing to skip.';
      default:
        return 'Could not join that room.';
    }
  }

  function enterRoomsHub() {
    teardownPeer();
    currentRoom = null;
    groupApi?.dispose?.(); // let the room take its window listeners with it
    groupApi = null;
    const hub = renderRooms({
      initialMode: chosenMode,
      // Staff get a second list: every live room, so a drop-in can reach a
      // private one or a minors' room. The server decides whether to answer;
      // this only decides whether to ask.
      isStaff,
      onRefreshAll: () => signaling?.send(ClientMsg.LIST_ALL_ROOMS),
      onCreate: ({ name, visibility, mode }) => signaling?.send(ClientMsg.CREATE_ROOM, { name, visibility, mode }),
      onRandom: (mode) => signaling?.send(ClientMsg.JOIN_RANDOM_ROOM, { mode }),
      // `invisible` is a staff-only request; the server ignores it from
      // anyone else, so passing it through unconditionally is safe.
      onJoin: ({ roomId, invisible }) => signaling?.send(ClientMsg.JOIN_ROOM, { roomId, invisible }),
      onJoinCode: (code) => signaling?.send(ClientMsg.JOIN_ROOM, { code }),
      onRefresh: () => signaling?.send(ClientMsg.LIST_ROOMS),
      onBack: showLanding,
    });
    roomsApi = hub.api;
    mount(root, hub.root);
    // The regulars bell in the hub's top bar, when the wave exists for this
    // band. Placed from here rather than inside rooms.js so the hub knows
    // nothing about regulars.
    if (social) hub.root.querySelector('.topbar')?.append(makeBell());
    // "Your community", above the rooms the site keeps open. Same reasoning:
    // the hub offers a slot, and knows nothing about communities.
    communityPanel = null;
    communityState = null;
    if (social && hub.api.communitySlot) {
      communitySection = renderCommunitySection({ client: communityClient, onOpen: openCommunity, onManage: openRegularsPanel });
      hub.api.communitySlot.append(communitySection.root);
    }
  }

  function enterGroupRoom(room) {
    teardownPeer();
    roomsApi = null;
    currentRoom = room;
    joinCode = null; // joined -> consume the invite and tidy the address bar
    try {
      if (/\/r\//.test(location.pathname)) history.replaceState({}, '', '/');
    } catch {
      /* ignore */
    }
    roomHandles.clear();
    sawVideoState = false; // a new room announces its video afresh
    mutedPeers.clear(); // mutes are per-room
    for (const mem of room.members || []) {
      if (mem && mem.peerId && mem.handle) roomHandles.set(mem.peerId, mem.handle);
    }
    const screen = renderGroupChat({
      room,
      // Who I am, for the activity frame (D0): the server's participant list
      // is peer ids, and the frame needs to know which one is this screen.
      selfId,
      handle,
      // Activities and the watch-together queue. Every rule is the server's;
      // these carry the request.
      onActivity: (op, slug, data) => signaling?.send(ClientMsg.ACTIVITY, { op, slug: slug || undefined, data }),
      onVideoQueue: (action, extra) => signaling?.send(ClientMsg.VIDEO_QUEUE, { action, ...(extra || {}) }),
      onSend: (text, replyTo) => {
        const id = newMessageId();
        signaling?.send(ClientMsg.CHAT, { text, id, replyTo: replyTo || null });
        return id; // the room renders your own copy with the same id
      },
      onReact: (id, emoji, on) => signaling?.send(ClientMsg.REACT, { id, emoji, on }),
      onUnsend: (id) => signaling?.send(ClientMsg.UNSEND, { id }),
      onFile: (file) => sendFile(file),
      onReport: (peerId, reason, image) =>
        signaling?.send(ClientMsg.REPORT, {
          reason,
          targetId: peerId,
          image: image || grabPeerFrame(peerId),
        }),
      onMicVolume: (gain) => {
        if (micGain) micGain.gain.value = gain;
      },
      onEffect, // background blur / filters / noise suppression -- same handler as 1:1
      // A microphone or camera coming on is the room hearing from you: the
      // server clears observer mode when the track is produced, and the
      // roster mark clears here without waiting for the round trip.
      // Only once a track is really live: enableMedia gives up quietly when
      // the permission is refused, and a "listening" mark that cleared on a
      // refused microphone would say something false about you.
      onEnableMic: async () => {
        await enableMedia({ audio: true });
        if ((localStream?.getAudioTracks() || []).some((track) => track.readyState === 'live')) {
          groupApi?.setSelfObserving?.(false);
        }
      },
      onEnableCam: async () => {
        await enableMedia({ video: true });
        if (outboundVideoTrack()) groupApi?.setSelfObserving?.(false);
        refreshTextOnlyLayout();
      },
      onCamToggle: (on) => {
        if (on) groupApi?.setSelfObserving?.(false);
        return onGroupCamToggle(on);
      },
      onToggleMute: (peerId) => {
        if (mutedPeers.has(peerId)) mutedPeers.delete(peerId);
        else mutedPeers.add(peerId);
        groupApi?.setMuted(peerId, mutedPeers.has(peerId));
      },
      onStageRequest: () => signaling?.send(ClientMsg.STAGE_REQUEST, {}),
      // The host's way onto the front page. Opens the shop at the promotion
      // section for THIS room; signing in happens there if it has to.
      onPromote: () => openPromo(room.code),
      onGoPrivate: (peerId) => signaling?.send(ClientMsg.GO_PRIVATE_REQUEST, { targetId: peerId }),
      onPrivateRespond: (fromId, accept) => signaling?.send(ClientMsg.GO_PRIVATE_RESPOND, { fromId, accept }),
      // A community's owner has the moderator's fifteen-minute kick inside
      // their own room (the server checks; this only shows the button).
      canModerate: isModerator || (!!room.community && room.ownerId === selfId),
      onKick: (peerId, reason, note) => signaling?.send(ClientMsg.KICK, { targetId: peerId, reason, note }),
      // Shared video. Every rule about who may do what is enforced on the
      // server; these just carry the request.
      onTyping: (state) => signaling?.send(ClientMsg.TYPING, { state }),
      onVideoPin: (url) => signaling?.send(ClientMsg.VIDEO_PIN, { url }),
      onVideoControl: (action, atMs) => signaling?.send(ClientMsg.VIDEO_CONTROL, { action, atMs }),
      onVideoShare: (on) => signaling?.send(ClientMsg.VIDEO_SHARE, { on }),
      onVoteStart: () => signaling?.send(ClientMsg.ROOM_VOTE_START),
      onVoteCast: (yes) => signaling?.send(ClientMsg.ROOM_VOTE_CAST, { yes }),
      // Observer mode and the quiet room. The text of the hello is fixed and
      // shared; the id is minted here so the local copy and the relayed one
      // are the same message.
      onObserverHello: () => {
        const id = newMessageId();
        signaling?.send(ClientMsg.OBSERVER_HELLO, { id });
        return id;
      },
      onFocusStart: (minutes) => signaling?.send(ClientMsg.FOCUS_START, { minutes }),
      onStatus: (text) => signaling?.send(ClientMsg.ROOM_STATUS, { text }),
      onLeave: () => {
        signaling?.send(ClientMsg.LEAVE_ROOM);
        enterRoomsHub();
      },
    });
    groupApi = screen.api;
    mount(root, screen.root);
    // A community's room carries its panel: members, who is here, the next
    // session, the owner's controls. Drawn from COMMUNITY_STATE, which follows
    // ROOM_JOINED (or already arrived, if this is a redraw).
    communitySection = null;
    communityPanel = null;
    if (room.community && groupApi.communitySlot) {
      const panel = renderCommunityPanel({
        client: communityClient,
        onKick: (peerId, reason, note) => signaling?.send(ClientMsg.KICK, { targetId: peerId, reason, note }),
        onManage: openRegularsPanel,
      });
      communityPanel = panel.api;
      groupApi.communitySlot.append(panel.root);
      if (communityState && communityState.id === room.community) communityPanel.update(communityState);
    }
    // Show yourself what everyone else in the room sees on you.
    groupApi.setModerator('self', isModerator);
    groupApi.setStaff('self', isStaff);
    groupApi.setPlus('self', isPlusMember);
    // Whoever created the room owns it to begin with.
    groupApi.setVideoOwner(room.ownerId === selfId, room.ownerId);
    videoSlots = room.videoSlots || null;
    videoWaiting = false;
    groupApi.setVideoSlots(slotsForUi(videoSlots));
    // A TEXT room never opens a camera or a microphone -- and it does not keep
    // one open either. The landing page may have captured both for a video
    // chat; walking into a text room with the camera still live showed the
    // person their own face in a room with no way to turn it off. Release it.
    // A video or audio room joined afterwards asks again through its own
    // camera and microphone buttons.
    if ((room.mode || ChatMode.VIDEO) === ChatMode.TEXT) releaseLocalMedia();
    // The QUIET room starts with the microphone muted and the camera off, and
    // the simplest true form of that is to hold neither: whatever the landing
    // captured is released, nothing is produced on entry, and the microphone
    // and camera buttons ask again when the person means it. Turning either
    // on is also what ends observer mode, on the server and here.
    if (room.quiet) releaseLocalMedia();
    if (localStream) groupApi.setSelf(localStream);
    groupApi.initEffects(effectPrefs); // sync blur/filter/denoise controls to saved prefs
    refreshSelfView(); // if an effect was already on, show the processed video immediately
    // Seed a tile for every existing member so each can be muted, even before media.
    for (const mem of room.members || []) {
      if (mem && mem.peerId && mem.peerId !== selfId) {
        groupApi.ensurePeer(mem.peerId, mem.handle);
        if (mem.moderator) groupApi.setModerator(mem.peerId, true);
        if (mem.staff) groupApi.setStaff(mem.peerId, true); // verified Lumi staff
        if (mem.plus) groupApi.setPlus(mem.peerId, true); // subtle Plus mark
        if (mem.observing) groupApi.setPeerObserving?.(mem.peerId, true);
        if (mem.status) groupApi.setPeerStatus?.(mem.peerId, mem.status);
      }
    }
    // A text room joins no media at all: no SFU transport, no camera or
    // microphone asked for, nothing sampled. Asking for a camera in a room
    // whose whole point is typing is exactly what having room kinds is meant
    // to stop.
    currentRoomMode = room.mode || ChatMode.VIDEO;
    if (currentRoomMode !== ChatMode.TEXT) setupGroupMedia();
  }

  // ---- Video slots in a group room ------------------------------------------
  // Membership is wide and cameras are metered (VIDEO_SLOTS). The server
  // refuses a camera when the slots are full; the person stays in with sound
  // and text, and is put on camera the moment a slot opens.
  function groupBitrate() {
    return maxVideoBitrate ? Math.min(maxVideoBitrate, GROUP_VIDEO_MAX_BITRATE) : GROUP_VIDEO_MAX_BITRATE;
  }
  function outboundVideoTrack() {
    const s = sendStream || localStream;
    return s ? s.getVideoTracks().find((track) => track.readyState === 'live') || null : null;
  }
  // The server names people by session id; your own tile is 'self'.
  function slotsForUi(slots) {
    if (!slots) return slots;
    return { ...slots, holders: (slots.holders || []).map((id) => (id === selfId ? 'self' : id)) };
  }
  function isSlotRefusal(err) {
    return /no_video_slot/.test(String(err && err.message ? err.message : err));
  }
  function onVideoRefused() {
    videoWaiting = true;
    groupApi?.setSelfVideoWaiting(true);
    toast(`All ${videoSlots?.max || VIDEO_SLOTS} camera slots are taken. You are in with sound and text, and you will be put on camera when one opens.`);
  }
  async function takeVideoSlot() {
    const track = outboundVideoTrack();
    if (!sfuPeer || !track || sfuPeer.hasProducer('video')) return;
    try {
      await sfuPeer.addTrack(track);
      videoWaiting = false;
      groupApi?.setSelfVideoWaiting(false);
      toast('A camera slot opened. You are on camera now.');
    } catch (err) {
      if (isSlotRefusal(err)) videoWaiting = true; // somebody was quicker; keep waiting
    }
  }
  async function onGroupCamToggle(on) {
    if (!sfuPeer) return;
    if (!on) {
      // Off means OFF: the producer closes, the slot frees, and nobody spends
      // bandwidth downloading a black frame from you.
      await sfuPeer.closeKind('video');
      videoWaiting = false;
      groupApi?.setSelfVideoWaiting(false);
      return;
    }
    const track = outboundVideoTrack();
    if (!track || sfuPeer.hasProducer('video')) return;
    try {
      await sfuPeer.addTrack(track);
    } catch (err) {
      if (isSlotRefusal(err)) onVideoRefused();
    }
  }

  function setupGroupMedia() {
    teardownSfuPeer();
    // An AUDIO room publishes voice only. Someone who chose video on the landing
    // still has a camera track open, and sending it into a room whose kind says
    // "no cameras" would break the promise the room made to everyone in it.
    let publish = sendStream || localStream;
    if (currentRoomMode === ChatMode.AUDIO && publish) {
      publish = new MediaStream(publish.getAudioTracks());
    }
    sfuPeer = new SfuPeer(
      (op, args) => sfuRpc(op, args),
      { localStream: publish, maxBitrate: groupBitrate() },
      {
        onProduceRefused: (kind, err) => {
          if (kind === 'video' && isSlotRefusal(err)) onVideoRefused();
        },
        onPeerStream: (peerId, stream) => {
          groupApi?.addPeer(peerId, stream, roomHandles.get(peerId));
          groupApi?.setPeerObserving?.(peerId, false); // a voice or a face is not quietly listening
          if (mutedPeers.has(peerId)) groupApi?.setMuted(peerId, true); // re-apply mute to a fresh tile
        },
        onPeerGone: (peerId) => groupApi?.removePeer(peerId),
        onFile: (peerId, { mime, blob, name }) => {
          if (mutedPeers.has(peerId)) return; // muted: drop their images/video/files
          groupApi?.addFile(roomHandles.get(peerId) || String(peerId).slice(0, 4), { mime, url: URL.createObjectURL(blob), name });
        },
        onState: () => {},
      },
    );
    sfuPeer.start().catch(() => toast('Could not connect to the room media. Try rejoining.'));
    // Walking in with a live microphone or camera is not quietly listening:
    // the server clears observer mode when the track is produced, and the
    // roster mark here should agree without waiting for it.
    if (publish && publish.getTracks().some((track) => track.readyState === 'live')) groupApi?.setSelfObserving?.(false);
    startModSampler();
    startAudioSampler();
  }

  function setupPeer(isCaller) {
    teardownPeer();
    remoteReady = false;
    pendingIce = [];
    peer = new PeerConnection(
      {
        iceServers: iceConfig.iceServers,
        iceTransportPolicy: iceConfig.iceTransportPolicy,
        isCaller,
        localStream: sendStream || localStream,
      },
      {
        onRemoteStream: (stream) => {
          chatApi?.setRemoteStream(stream);
          remoteHasVideo = !!stream && (stream.getVideoTracks?.() || []).length > 0;
          refreshTextOnlyLayout();
          chatApi?.hideStatus();
          chatApi?.pulse();
          chatApi?.focusInput();
        },
        onIce: (candidate) => signaling?.send(ClientMsg.SIGNAL, { kind: 'ice', data: candidate }),
        onFile: ({ mime, blob, name }) => {
          const url = URL.createObjectURL(blob);
          chatApi?.addFile('them', { mime, url, name });
        },
        onState: (state) => {
          if (state === 'connected') chatApi?.hideStatus();
          if (state === 'failed') toast('Connection trouble. Finding someone new...');
        },
      },
    );

    if (isCaller) {
      peer
        .createOffer()
        .then((offer) => signaling?.send(ClientMsg.SIGNAL, { kind: 'offer', data: offer }))
        .catch(() => toast('Could not start the call.'));
    }
  }

  async function handleSignal({ kind, data }) {
    if (!peer) return;
    try {
      if (kind === 'offer') {
        const answer = await peer.acceptOffer(data);
        signaling?.send(ClientMsg.SIGNAL, { kind: 'answer', data: answer });
        remoteReady = true;
        flushIce();
      } else if (kind === 'answer') {
        await peer.acceptAnswer(data);
        remoteReady = true;
        flushIce();
      } else if (kind === 'ice') {
        if (remoteReady) peer.addIce(data);
        else pendingIce.push(data);
      }
    } catch {
      /* ignore malformed signaling */
    }
  }

  function flushIce() {
    for (const c of pendingIce) peer?.addIce(c);
    pendingIce = [];
  }

  // Files go THROUGH the server, never peer to peer: the server is the only
  // place they can be classified before the other person sees them, and it
  // archives them as evidence. A file the server blocks is simply never
  // delivered -- so the receiving side has nothing to un-see.
  async function sendFile(file) {
    if (!isShareableFile(file.type, file.size)) {
      // Caught here so a 25 MB video is never uploaded just to be refused.
      toast(
        file.type && !file.type.startsWith('image/')
          ? 'Only images can be shared. Video and audio cannot be sent on Lumi.'
          : 'Only images can be shared, up to 25 MB.',
      );
      return;
    }
    if (!shareKey) {
      toast('Wait until you are connected to share.');
      return;
    }
    toast('Checking that file...');
    let res;
    try {
      res = await fetch(`${SERVER_HTTP}/api/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-Share-Key': shareKey,
          'X-Share-Mime': file.type,
          'X-Share-Name': encodeURIComponent(file.name || ''),
        },
        body: file,
      });
    } catch {
      toast('Could not share that file.');
      return;
    }
    // Read the body ONCE, out here, because the success path needs it too. It
    // used to be declared inside the failure branch and read after it, so every
    // accepted upload threw ReferenceError: the file reached the other person
    // and the sender's own copy never appeared, which reads as "it did not
    // send" and invites them to send it again.
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      toast(body.message || 'That file could not be shared.');
      return;
    }
    // Only show it on my own side once the server accepted it. The server's
    // msgId keys my copy the same way it keys everyone else's, so a reaction
    // or an unsend lands on the same message on every screen.
    const me = { mime: file.type, url: URL.createObjectURL(file), name: file.name };
    const opts = { id: body.msgId };
    if (groupApi) groupApi.addFile('me', me, opts);
    else chatApi?.addFile('me', me, opts);
  }

  /** Fetch a file the server has cleared, and show it. */
  async function receiveSharedFile(m) {
    if (!m || !m.shareId || !shareKey) return;
    try {
      const res = await fetch(`${SERVER_HTTP}/api/share/${encodeURIComponent(m.shareId)}`, {
        headers: { 'X-Share-Key': shareKey },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const item = { mime: m.mime || blob.type, url: URL.createObjectURL(blob), name: m.name || 'file' };
      const opts = { id: m.msgId, peerId: m.from };
      if (groupApi) groupApi.addFile(roomHandles.get(m.from) || m.from || 'them', item, opts);
      else chatApi?.addFile('them', item, opts);
    } catch {
      /* the file simply does not appear */
    }
  }

  /**
   * Turn on mic and/or camera mid-chat (for a user who started in audio or text).
   * Captures the missing tracks, routes new audio through the gain node, adds them
   * to the local + send streams, and produces them to the SFU so the partner sees
   * them without a rematch.
   */
  async function enableMedia({ audio = false, video = false }) {
    if (!sfuEnabled || !sfuPeer) {
      toast('Wait until you are connected.');
      return;
    }
    if (video && !videoAllowed) return;
    const haveAudio = !!(localStream && localStream.getAudioTracks().length);
    const haveVideo = !!(localStream && localStream.getVideoTracks().length);
    const wantAudio = audio && !haveAudio;
    const wantVideo = video && !haveVideo;
    if (!wantAudio && !wantVideo) return;

    let captured;
    try {
      captured = await navigator.mediaDevices.getUserMedia({ audio: wantAudio, video: wantVideo });
    } catch {
      toast(wantVideo ? 'Lumi needs camera permission for that.' : 'Lumi needs microphone permission for that.');
      return;
    }
    if (!localStream) localStream = new MediaStream();
    if (!sendStream) sendStream = new MediaStream();
    const toProduce = [];

    for (const v of captured.getVideoTracks()) {
      rawCamTrack = v;
      localStream.addTrack(v); // self-view stays the raw camera
      let outV = v;
      if (videoFxOn()) {
        try {
          videoEnhancer = createVideoEnhancer(v, { segmenter: bgSegmenter });
          videoEnhancer.setBlur(!!effectPrefs.blur);
          videoEnhancer.setFilter(effectPrefs.filter);
           
          await videoEnhancer.start();
          outV = videoEnhancer.track;
        } catch {
          outV = v;
          videoEnhancer = null;
        }
      }
      sendStream.addTrack(outV);
      toProduce.push(outV);
    }
    for (const a of captured.getAudioTracks()) {
      localStream.addTrack(a); // self-view is muted, so this is harmless
      let outTrack = a;
      try {
        if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        if (!micGain) micGain = audioCtx.createGain();
        const src = audioCtx.createMediaStreamSource(new MediaStream([a]));
        const dest = audioCtx.createMediaStreamDestination();
        // Noise suppression (#9) sits between the mic and the gain; bypassed unless on.
        audioEnhancer = createAudioEnhancer(audioCtx, src);
        audioEnhancer.setEnabled(!!effectPrefs.denoise);
        audioEnhancer.output.connect(micGain).connect(dest);
        audioCtx.resume?.();
        outTrack = dest.stream.getAudioTracks()[0]; // gain-routed -> the volume slider applies
      } catch {
        outTrack = a; // Web Audio unavailable -- send the raw mic
      }
      sendStream.addTrack(outTrack);
      toProduce.push(outTrack);
    }

    for (const track of toProduce) {
      // In a 1:1, turning the camera on is your half of the reveal. The track
      // goes up regardless; the server shows it to them only once both have.
      if (track.kind === 'video' && inOneOnOne() && !reveal.both) requestReveal();
      try {
         
        await sfuPeer.addTrack(track);
      } catch (err) {
        // A camera refused for want of a slot is not a failure to report as
        // one: the person is in, with sound, and will be put on camera later.
        if (track.kind === 'video' && isSlotRefusal(err)) onVideoRefused();
        else toast('Could not turn that on. Please try again.');
      }
    }
    chatApi?.setLocalStream(localStream);
    pushRevealUi(); // a camera that just appeared changes what the reveal can offer
  }

  // ---- Media effects (#8 background blur, #9 noise suppression, #11 filters) -------
  // A pluggable selfie segmenter for background blur. Null until a self-hosted
  // segmentation model is bundled (then blur composites a sharp person over a blurred
  // background); without it, blur is a safe no-op and the filters still apply.
  // Resume the mic AudioContext on the user's next interaction (it can start suspended when
  // created behind the camera-permission prompt). Idempotent; self-removes once running.
  function armAudioContextResume() {
    const tryResume = () => {
      if (!audioCtx) return;
      audioCtx
        .resume?.()
        .then(() => {
          if (!audioCtx || audioCtx.state === 'running') {
            document.removeEventListener('pointerdown', tryResume, true);
            document.removeEventListener('keydown', tryResume, true);
          }
        })
        .catch(() => {});
    };
    document.addEventListener('pointerdown', tryResume, true);
    document.addEventListener('keydown', tryResume, true);
  }

  // Background blur uses a pluggable segmenter. The real one is a self-hosted
  // selfie-segmentation model (loaded lazily, on first use, because it pulls a
  // few megabytes of wasm); until it is ready -- or on any device where it
  // cannot run -- the no-model focus segmenter keeps blur working. The enhancer
  // holds THIS stable wrapper, so the upgrade applies to a chat already running.
  const focusSegmenter = createFocusSegmenter();
  let mlSegmenter = null;
  let mlSegmenterTried = false;
  const bgSegmenter = (video, w, h) => (mlSegmenter || focusSegmenter)(video, w, h);
  async function ensureMlSegmenter() {
    if (mlSegmenterTried) return;
    mlSegmenterTried = true;
    try {
      const { createSelfieSegmenter } = await import('./selfie-segmenter.js');
      const ml = await createSelfieSegmenter();
      if (ml) mlSegmenter = ml;
    } catch {
      /* keep the focus fallback */
    }
  }
  function videoFxOn() {
    return !!effectPrefs.blur || (!!effectPrefs.filter && effectPrefs.filter !== 'none');
  }
  // Point the self-view at the PROCESSED video (the enhancer's canvas track) when an effect
  // is active, else the raw camera -- so the user actually sees their filter / blur.
  function refreshSelfView() {
    const vt = (videoEnhancer && videoEnhancer.track) || rawCamTrack;
    if (!vt) return;
    chatApi?.setSelfVideo?.(vt);
    groupApi?.setSelfVideo?.(vt);
  }
  async function onEffect(kind, value) {
    if (kind === 'denoise') {
      effectPrefs.denoise = !!value;
      audioEnhancer?.setEnabled(!!value);
    } else if (kind === 'blur') {
      effectPrefs.blur = !!value;
      if (value) ensureMlSegmenter(); // load the model in the background
      await applyVideoEffects();
    } else if (kind === 'filter') {
      effectPrefs.filter = value || 'none';
      await applyVideoEffects();
    }
    try {
      localStorage.setItem('lumi.effects', JSON.stringify(effectPrefs));
    } catch {
      /* ignore */
    }
  }
  // Bring the live SENT video in line with the current effect prefs -- build or tear
  // down the canvas processor and swap the produced track, without dropping the call.
  async function applyVideoEffects() {
    if (!rawCamTrack) return; // no camera -> prefs apply on the next capture
    const want = videoFxOn();
    if (want && !videoEnhancer) {
      try {
        videoEnhancer = createVideoEnhancer(rawCamTrack, { segmenter: bgSegmenter });
        videoEnhancer.setBlur(!!effectPrefs.blur);
        videoEnhancer.setFilter(effectPrefs.filter);
        await videoEnhancer.start();
        await sfuPeer?.replaceTrack('video', videoEnhancer.track);
      } catch {
        videoEnhancer = null;
      }
    } else if (!want && videoEnhancer) {
      try {
        await sfuPeer?.replaceTrack('video', rawCamTrack);
        videoEnhancer.stop();
      } catch {
        /* ignore */
      }
      videoEnhancer = null;
    } else if (videoEnhancer) {
      videoEnhancer.setBlur(!!effectPrefs.blur);
      videoEnhancer.setFilter(effectPrefs.filter);
    }
    refreshSelfView(); // show the effect on the user's own preview, not just to the peer
  }

  // ---- Image moderation: periodically sample the local camera and send each frame to
  // the server, which classifies it with the self-hosted moderator. Runs only when the
  // server advertises a moderator backend (moderateImages) and a live camera is on.
  const MOD_SAMPLE_MS = 20000;
  function startModSampler() {
    if (!moderateImages || modSampler) return;
    if (!modSampleVideo) {
      modSampleVideo = document.createElement('video');
      modSampleVideo.muted = true;
      modSampleVideo.playsInline = true;
    }
    const canvas = document.createElement('canvas');
    const cctx = canvas.getContext('2d');
    // Stay inside the server's frame ceiling (b64 <= 6M chars ~= 4.5 MB JPEG):
    // a frame that overshoots is re-encoded smaller, NEVER dropped.
    const B64_BUDGET = 5_500_000;
    const encodeFrame = (scale, quality) =>
      new Promise((resolve) => {
        const vw = modSampleVideo.videoWidth;
        const vh = modSampleVideo.videoHeight;
        canvas.width = Math.max(1, Math.round(vw * scale));
        canvas.height = Math.max(1, Math.round(vh * scale));
        cctx.drawImage(modSampleVideo, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
      });
    // A frame with no variation carries nothing to moderate, and the classifier
    // returns confident nonsense on one -- which is how solid black frames ended
    // up in the queue labelled nsfw, each one a false accusation against a real
    // person sitting next to a Ban button. Checked on a 32x32 copy, so the cost
    // is negligible.
    const probe = document.createElement('canvas');
    probe.width = 32;
    probe.height = 32;
    const pctx = probe.getContext('2d', { willReadFrequently: true });
    const frameIsBlank = () => {
      try {
        pctx.drawImage(modSampleVideo, 0, 0, 32, 32);
        const { data } = pctx.getImageData(0, 0, 32, 32);
        let sum = 0;
        let sumSq = 0;
        const n = 32 * 32;
        for (let i = 0; i < data.length; i += 4) {
          const luma = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          sum += luma;
          sumSq += luma * luma;
        }
        const mean = sum / n;
        const variance = sumSq / n - mean * mean;
        return variance < 12; // near-uniform: a lens cap, a warming sensor, a disabled track
      } catch {
        return false; // never let the guard itself stop moderation
      }
    };

    const sample = async () => {
      const track = localStream?.getVideoTracks?.()[0];
      if (!signaling || !track || track.readyState !== 'live') return;
      // A camera nobody has opened is not moderated (the server drops such
      // frames too). Rooms have no reveal.
      if (inOneOnOne() && !reveal.both) return;
      // Camera off. The button DISABLES the track rather than stopping it, so it
      // stays "live" while emitting black -- moderating that is pointless.
      if (track.enabled === false) return;
      try {
        if (modSampleVideo.srcObject !== localStream) {
          modSampleVideo.srcObject = localStream;
          modSampleVideo.play?.().catch(() => {});
        }
        if (!modSampleVideo.videoWidth) return;
        if (frameIsBlank()) return;
        // Evidence-grade capture at the camera's FULL native resolution -- a
        // human judges this exact frame in the admin queue. The classifier
        // downscales internally either way. Quality steps down only if the
        // encoded frame would blow the transport budget.
        let blob = null;
        for (const [scale, quality] of [[1, 1], [1, 0.85], [1, 0.65], [0.7, 0.6]]) {
           
          blob = await encodeFrame(scale, quality);
          if (blob && (blob.size * 4) / 3 < B64_BUDGET) break;
        }
        if (!blob) return;
        const reader = new FileReader();
        reader.onload = () => {
          const b64 = String(reader.result).split(',')[1] || '';
          if (b64 && b64.length < B64_BUDGET + 500_000) {
            signaling?.send(ClientMsg.MOD_FRAME, { image: b64 });
          }
        };
        reader.readAsDataURL(blob);
      } catch {
        /* a sampling hiccup is non-fatal */
      }
    };
    modSampler = setInterval(sample, MOD_SAMPLE_MS);
    setTimeout(sample, 4000); // first sample a few seconds after connecting
  }
  function stopModSampler() {
    if (modSampler) {
      clearInterval(modSampler);
      modSampler = null;
    }
    if (modSampleVideo) modSampleVideo.srcObject = null;
  }

  // ---- Audio moderation + live captions: record short mic clips and send them to the
  // server, which transcribes via the self-hosted ASR. Runs only when the server
  // advertises an ASR backend and the mic is live.
  const ASR_CLIP_MS = 5000;
  function pickAudioOpts() {
    const cands = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];
    for (const c of cands) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) return { mimeType: c };
    }
    return {};
  }
  function startAudioSampler() {
    if (!asrEnabled || audioActive || typeof MediaRecorder === 'undefined') return;
    audioActive = true;
    const loop = () => {
      if (!audioActive) return;
      const track = localStream?.getAudioTracks?.()[0];
      if (!signaling || !track || track.readyState !== 'live') {
        audioTimer = setTimeout(loop, 1500);
        return;
      }
      try {
        const rec = new MediaRecorder(new MediaStream([track]), pickAudioOpts());
        const chunks = [];
        rec.ondataavailable = (e) => {
          if (e.data && e.data.size) chunks.push(e.data);
        };
        rec.onstop = () => {
          audioRecorder = null;
          const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
          if (blob.size) {
            const reader = new FileReader();
            reader.onload = () => {
              const b64 = String(reader.result).split(',')[1] || '';
              if (b64) signaling?.send(ClientMsg.MOD_AUDIO, { audio: b64 });
            };
            reader.readAsDataURL(blob);
          }
          if (audioActive) audioTimer = setTimeout(loop, 400);
        };
        audioRecorder = rec;
        rec.start();
        setTimeout(() => {
          try {
            if (rec.state === 'recording') rec.stop();
          } catch {
            /* ignore */
          }
        }, ASR_CLIP_MS);
      } catch {
        audioTimer = setTimeout(loop, 2000);
      }
    };
    audioTimer = setTimeout(loop, 2500);
  }
  function stopAudioSampler() {
    audioActive = false;
    clearTimeout(audioTimer);
    try {
      if (audioRecorder?.state === 'recording') audioRecorder.stop();
    } catch {
      /* ignore */
    }
    audioRecorder = null;
  }

  function teardownPeer() {
    peer?.close();
    peer = null;
    remoteReady = false;
    pendingIce = [];
    teardownSfuPeer();
    stopModSampler();
    stopAudioSampler();
    if (chatApi) chatApi.setRemoteStream(null);
    remoteHasVideo = false;
  }

  // Let go of the camera and the microphone: every track stopped, so the
  // camera light goes out, and every processor built on them torn down. Used
  // on leaving, and on walking into a TEXT room -- a room whose whole point is
  // typing must not keep a camera open that was granted for something else.
  function releaseLocalMedia() {
    if (videoEnhancer) {
      videoEnhancer.stop();
      videoEnhancer = null;
    }
    audioEnhancer = null;
    rawCamTrack = null;
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
      localStream = null;
    }
    if (audioCtx) {
      audioCtx.close().catch(() => {});
      audioCtx = null;
    }
    micGain = null;
    sendStream = null;
  }

  function cleanup() {
    // The age proof is NOT cleared here. It lives per tab (sessionStorage) and
    // for two hours, which bounds a shared device well enough; clearing it on
    // every return to the landing would send everyone who changes mode back
    // through the age estimator, and that costs more than it protects for now.
    teardownPeer();
    clearTimeout(slowDownTimer);
    slowDownTimer = null;
    cancelQuietHint();
    cancelInviteOffer();
    signaling?.send?.(ClientMsg.LEAVE);
    signaling?.close();
    signaling = null;
    // Leaving on purpose is not something to recover from. Signaling.close()
    // already stops the close event, but a reconnect may be mid-flight in its
    // own timer, and it checks these.
    reconnecting = false;
    resumeSeed = null;
    releaseLocalMedia();
    chatApi = null;
    roomsApi = null;
    groupApi?.dispose?.(); // let the room take its window listeners with it
    groupApi = null;
    currentRoom = null;
    roomHandles.clear();
    intent = '1on1';
    // No call survives the socket: the sheets come down with it. Before the
    // panel closes, so its closing cannot read "a call just ended" and search.
    callsOn = false;
    inCall = false;
    pairedNow = false;
    afterCall = false;
    callingSheet?.close();
    offerSheet?.close();
    callingSheet = null;
    offerSheet = null;
    // The bells went with their screens; the panel, if open, now has no socket.
    bells.clear();
    lastPairId = null;
    social = false;
    messagesOn = false;
    regularsPanel?.close?.();
    regularsPanel = null;
    communityCard = null;
    communityPanel = null;
    communitySection = null;
    communityState = null;
  }

  async function initAccount() {
    await account.refresh().catch(() => {});
    applyCosmetics();
    // Handle a return from checkout (stub completes in place; Stripe returns here).
    const params = new URLSearchParams(location.search);
    const flow = params.get('billing');
    if (flow === 'stub' && params.get('token')) {
      const r = await account.stubComplete(params.get('token'));
      if (r.status === 200) toast('Purchase complete.');
      applyCosmetics();
    } else if (flow === 'success') {
      await account.refresh().catch(() => {});
      toast('Thanks! Your purchase is active.');
      applyCosmetics();
    }
    if (flow) history.replaceState({}, '', location.pathname);
    // Deep link from the Lumi Plus page to the in-app shop (cosmetics, Room Pro, donate).
    if (params.get('shop') === 'open') {
      openPlus();
      history.replaceState({}, '', location.pathname);
    }
    offerAccountName();
    syncAccountLink();
    checkNotice(); // celebrate an admin-granted Plus on load
    startNoticeWatch(); // and catch one granted while the tab is open
  }
  initAccount();

  return { showLanding };
}
